// Owns ALL mutable game state, the sim loop, and save I/O. The only module that reads the
// wall clock or touches storage. (TECHNICAL_DESIGN.md §1, §7, §9, §10, §12.)
import { Application, ColorMatrixFilter, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import {
  ATTACK_ARC_DOT, ATTACK_ARC_RANGE, ATTACK_CD, BOW_CD, BOW_DMG, BOW_RANGE, BOW_SPEED, POINT_BLANK,
  CHUNK_MARGIN, CORRUPTION_RADIUS, CYCLE_LEN, DAY_LEN, DESPAWN_DIST, HARVEST_RANGE, HITSTOP,
  KNOCKBACK, MAX_CHUNK_SPRITES, MAX_FOOD_BUFFS, MOB_ATTACK_CD, MOB_CAP, MOB_WINDUP,
  MOBILE_MAX_EDGE, NODE_RESPAWN_MAX, NODE_RESPAWN_MIN, PLAYER_HP, PLAYER_IFRAMES, PLAYER_SPEED,
  REGEN_PER_S, RESPAWN_GOLD_LOSS, SPAWN_INTERVAL, SPAWN_MAX_DIST, SPAWN_MIN_DIST, TILE,
  ZOOM_DESKTOP, ZOOM_MOBILE, DUNGEON_ZOOM_MULT, chunkOf, tileToWorld, worldToTile,
} from './const';
import { MOBS, nextState, rollDrops, spawnCandidate } from './entities';
import {
  ITEMS, canCraft, gatherPower, makeCraftedGear, makeFoodBuff, nodeYield, recipeById, requiredPickaxe, resolveFood,
} from './items';
import { Rng, hash3i, toUnit } from './rng';
import { exportCode, freshSave, groveThreshold, importCode, readSave, writeSave } from './save';
import { FISTS_ATK, computeHit, computeStats, xpToLevel } from './stats';
import { bakeChunkSprite, bakeDungeonSprite, bakePlayer, tex } from './textures';
import { biomeAt, corruptionAt, findSpawn, genChunk, isSolidTile, tileOfBiome } from './world';
import { DUNGEON_MAX_FLOOR, type BossKind, type DungeonFloor, bossKindForFloor, bossName, genFloor, isWall, mobStatScale } from './dungeon';
import {
  EMPOWER_MAX, LEGENDARIES, SLOTS, empowerCost, empoweredBase, equippedAffixIds, equippedAffixSum,
  getEquipped, motifEffects, relicEffects, rollGear, rollRelic, salvageValue, RELIC_ICON,
  type RelicPower,
} from './loot';
import { accrueBlessing, dayUTC, todaysBlessing } from './blessing';
import type {
  Chunk, GearItem, GroundDrop, ItemId, LegendaryId, Mob, Placed, Projectile, Rarity, RelicItem, SaveData,
  Slot, Stats, WorldNode,
} from './types';
import {
  BLESSINGS, BOSS_WINDUP, CAMPFIRE_HEAL_PER_S, CAMPFIRE_RADIUS, CHEST_GEAR_CHANCE, CHEST_OPEN_RANGE,
  DUNGEON_ENTER_RANGE, EMBER_CAP_HOURS, EMBER_PER_HOUR, EMPOWER_AFFIX_STEP, MOB_GEAR_CHANCE, PITY_N,
  GROVE_TOTEM_COSTS, GROVE_START_SAT, GROVE_MAXHP_PER_STAGE, GROVE_REGEN_PER_STAGE, GROVE_TIER_BONUS,
  OVERWORLD_GEAR_CHANCE, OVERWORLD_GEAR_FLOOR, OVERWORLD_GEAR_MOBS,
  PLACE_RANGE, SAFE_ZONE_RADIUS, SANCTUARY_BASE_GOLD, SANCTUARY_BASE_STONE, SANCTUARY_MAX_LEVEL,
  SANCTUARY_STEP, SATCHEL_DEATH_RATE, STAIRS_RANGE, TIER_LOOT_SHIFT, TIER_MAX, TIER_MOB_MULT,
  WARMED_BASE_S, WARMED_COMFORT_RADIUS, WARMED_PER_COMFORT_S, WARMED_SPD_PCT, WARMED_XP_MULT,
} from './const';

export interface Intent {
  move: { x: number; y: number };
  interact: boolean;
}
type Listener = (payload?: unknown) => void;
// Flavour for each grove-restoration stage (the final stage has its own line in feedGrove).
const GROVE_LINES = [
  'One ember returned. The meadow around your camp softens back to green.',
  'The second totem wakes. Fireflies drift back into the dark.',
  'Colour deepens across the grove — the roots remember warmth.',
  'The forest re-lights, leaf by leaf. The dimming slows.',
];
interface NodeSprite { node: WorldNode; sprite: Sprite; hp: number; label?: Text; glow?: Sprite }
interface MobInst { mob: Mob; sprite: Sprite; label: Container; barFill: Sprite; barW: number; aura?: Sprite; baseScale: number }
interface DropInst { drop: GroundDrop; sprite: Sprite }
interface ProjInst { proj: Projectile; sprite: Sprite }
interface FxItem { g: Sprite | Text; diesAtReal: number; vy: number }

const SKILL_NAMES: Record<string, string> = { wood: 'Woodcutting', mine: 'Mining', combat: 'Combat', craft: 'Crafting' };

export class Game {
  save!: SaveData;
  running = false;
  weapon: 'melee' | 'bow' = 'melee';
  ambientSpawns = true; // tests disable this to isolate mob counts
  safeZone = true; // the home sanctuary; combat tests disable it to fight anywhere
  mode: 'overworld' | 'dungeon' = 'overworld';
  pendingBlessing = false; // UI shows the pick-1-of-3 picker when true
  pendingPortal = false;   // UI shows the "enter the portal → travel home?" confirm when true
  intent: Intent = { move: { x: 0, y: 0 }, interact: false };
  zoom = ZOOM_DESKTOP;

  private app: Application;
  private worldRoot: Container;
  private overGround: Container; // overworld chunk sprites + fire glows + corruption
  private overEnv: Container;    // overworld nodes + placeables (y-sorted)
  private dunGround: Container;  // dungeon floor sprite
  private dunEnv: Container;     // dungeon walls/chest/stairs (y-sorted)
  private actorLayer: Container; // player + mobs + drops + projectiles (shared, y-sorted)
  private groveFilter: ColorMatrixFilter; // drains/returns the world's colour with grove restoration
  private fxLayer: Container;
  private playerSprite!: Sprite;
  private safeZoneGfx?: Graphics;
  private dunTorch?: Sprite; // warm light that follows the player in the dark of a dungeon
  private bowSprite?: Sprite;   // held bow overlay, rotated to the facing direction
  private meleeSprite?: Sprite;  // held sword overlay (when armed with a melee weapon)
  private playerFaceSign = 1; // -1 when facing left, +1 right (kept while walking up/down)
  private chunkSprites = new Map<string, Sprite>();
  private chunks = new Map<string, Chunk>();
  private nodeSprites = new Map<string, NodeSprite>();
  private corruptionGfx = new Map<string, Sprite>();
  private mobs = new Map<number, MobInst>();
  private drops = new Map<number, DropInst>();
  private projectiles = new Map<number, ProjInst>();
  private placedSprites = new Map<string, Sprite[]>(); // "tx,ty" → [sprite, glow?]
  private walls = new Set<string>(); // "tx,ty" of wooden walls, for collision
  private fx: FxItem[] = [];
  private eid = 1;
  private facing = { x: 0, y: 1 };
  private attackCdUntil = 0;
  private iframesUntil = 0;
  private playerHurtUntil = 0; // brief red flash after taking a hit
  private spawnAccum = 0;
  private hitstopLeft = 0;
  private shake = { mag: 0, until: 0 };
  private combatRng: Rng;
  private spawnRng: Rng;
  private lootRng: Rng; // injectable, NOT world-seed (anti save-scum)
  private equippedLegendaries = new Set<LegendaryId>();
  private motif = { lifestealPct: 0, emberPct: 0, hpFlat: 0 };
  private relic: RelicPower = { regenPerS: 0, xpPct: 0, dodgePct: 0, spdPct: 0, goldPct: 0 };
  private floorEnterGameS = 0; // best-time stamp (not serialized)
  // ---- dungeon runtime state (floors are disposable; never serialized) ----
  private floor = 0;
  private visitNonce = 1;
  private dunReturn = { x: 0, y: 0 };
  private dunWalls = new Set<string>();
  private dunFloor: DungeonFloor | null = null;
  private chestTiles = new Map<string, Sprite>();
  private stairsTile = { tx: 0, ty: 0 };
  private dunTraps = new Set<string>(); // "tx,ty" of spike-trap tiles on the current floor
  private bossEid = 0; // >0 while a boss lives on this floor
  private runCleared = false; // a boss fell this run → flame the dungeon on exit
  private runBuffs = { atk: 0, maxHp: 0, spdPct: 0 };
  private acc = 0;
  private readonly STEP = 1 / 60;
  private lastPerf = { fps: 0, frames: 0, t: 0 };
  private listeners = new Map<string, Set<Listener>>();
  // milestone tracker — one substrate feeding SFX/narrative/onboarding/pacing. Each milestone is a
  // pure predicate over the (already-persisted) save, so a reload of a progressed save initializes
  // it as already-fired and narrative never replays. No new save field, no schema bump.
  private milestones = new Map<string, { atGameS: number; atMs: number; pre: boolean }>();

  constructor(app: Application) {
    this.app = app;
    this.worldRoot = new Container();
    this.overGround = new Container();
    this.overEnv = new Container(); this.overEnv.sortableChildren = true;
    this.dunGround = new Container();
    this.dunEnv = new Container(); this.dunEnv.sortableChildren = true;
    this.actorLayer = new Container(); this.actorLayer.sortableChildren = true;
    this.fxLayer = new Container();
    this.worldRoot.addChild(this.overGround, this.dunGround, this.overEnv, this.dunEnv, this.actorLayer, this.fxLayer);
    app.stage.addChild(this.worldRoot);
    this.groveFilter = new ColorMatrixFilter();
    // combat & spawn RNG: boot-seeded, NON-deterministic (anti save-scum); tests inject seeds.
    const boot = (globalThis.crypto?.getRandomValues?.(new Uint32Array(1))?.[0] ?? 12345) | 0;
    this.combatRng = new Rng(boot ^ 0xa5a5);
    this.spawnRng = new Rng(boot ^ 0x5a5a);
    this.lootRng = new Rng(boot ^ 0x3c3c);
    this.visitNonce = (boot & 0x7fffffff) | 1; // fresh floors per visit; incremented per enter/descend
  }
  setCombatSeed(n: number): void { this.combatRng = new Rng(n | 0); }
  setSpawnSeed(n: number): void { this.spawnRng = new Rng(n | 0); }
  setLootSeed(n: number): void { this.lootRng = new Rng(n | 0); }

  // ---- event bus ----
  on(ev: string, fn: Listener): void {
    (this.listeners.get(ev) ?? this.listeners.set(ev, new Set()).get(ev)!).add(fn);
  }
  private emit(ev: string, payload?: unknown): void { this.listeners.get(ev)?.forEach((fn) => fn(payload)); }

  // ---- milestones (narrative + onboarding + pacing telemetry share this one mechanism) ----
  /** True-once conditions, each derivable from persisted state (so reloads don't replay them). */
  private milestonePredicates(): Record<string, boolean> {
    const s = this.save;
    return {
      // firstWood is only false on a brand-new save; any progression marker flips it true.
      firstWood: (s.inventory['wood'] ?? 0) > 0 || s.tools.axe > 0 || s.tools.pickaxe > 0
        || s.world.placed.length > 0 || s.dungeon.deepest > 0,
      campfire: s.world.placed.some((p) => p.kind === 'campfire'),
      stoneTools: s.tools.axe >= 2 || s.tools.pickaxe >= 2,
      firstNightfall: s.clock.gameS >= DAY_LEN, // gameS is monotonic → survives reload
      firstDungeon: s.dungeon.deepest > 0 || this.mode === 'dungeon',
      firstBoss: s.dungeon.checkpoints.length > 0,
      firstLegendary: s.gear.some((g) => g.legendary) || s.satchel.gear.some((g) => g.legendary),
    };
  }
  /** Seed the fired-set from the loaded save so pre-existing milestones never emit narrative. */
  private initMilestones(): void {
    this.milestones.clear();
    const p = this.milestonePredicates();
    for (const id in p) if (p[id]) this.milestones.set(id, { atGameS: this.save.clock.gameS, atMs: Date.now(), pre: true });
  }
  /** Fire (once) any milestone whose predicate just became true this session. */
  private checkMilestones(): void {
    const p = this.milestonePredicates();
    for (const id in p) {
      if (p[id] && !this.milestones.has(id)) {
        this.milestones.set(id, { atGameS: this.save.clock.gameS, atMs: Date.now(), pre: false });
        this.emit('milestone', { id });
      }
    }
  }
  /** Milestone game-time stamps for pacing telemetry; null = not reached this session. */
  pacing(): Record<string, number | null> {
    const out: Record<string, number | null> = {};
    for (const id in this.milestonePredicates()) {
      const m = this.milestones.get(id);
      out[id] = m && !m.pre ? Math.round(m.atGameS * 10) / 10 : null;
    }
    return out;
  }

  // ---- deterministic time helpers (tests + fast-forward) ----
  /** Advance the clock to just past dusk of the current cycle. */
  skipToNight(): void {
    const t = this.save.clock.gameS % CYCLE_LEN;
    if (t < DAY_LEN) this.save.clock.gameS += (DAY_LEN - t) + 0.1;
    this.checkMilestones();
  }
  skipDays(n: number): void { this.save.clock.gameS += Math.max(0, n) * CYCLE_LEN; this.checkMilestones(); }

  /** Switch weapon for user input — blocks the bow until one is crafted (else it deals 0). */
  setWeapon(w: 'melee' | 'bow'): boolean {
    if (w === 'bow' && !getEquipped(this.save.gear, this.save.equipped, 'subweapon')) {
      this.emit('toast', { icon: '🏹', text: 'Equip a bow in your sub-weapon slot (👤)' });
      return false;
    }
    this.weapon = w;
    return true;
  }

  // ---- home portal network ----
  /** Guarantee a portal stands on the home tile — the anchor the traveller emerges from. */
  private ensureHomeAnchor(): void {
    const s = this.save;
    const htx = worldToTile(s.home.x), hty = worldToTile(s.home.y);
    if (s.world.placed.some((p) => p.kind === 'home_portal' && p.tx === htx && p.ty === hty)) return;
    const placed: Placed = { kind: 'home_portal', tx: htx, ty: hty };
    s.world.placed.push(placed);
    if (this.running && this.mode === 'overworld') this.spawnPlacedSprite(placed);
  }
  /** Confirm travel: step through the portal and emerge at the home anchor. */
  teleportHome(): void {
    this.pendingPortal = false;
    if (this.mode !== 'overworld') return;
    this.ensureHomeAnchor();
    const s = this.save;
    s.player.x = s.home.x; s.player.y = s.home.y;
    this.spawnPop(s.home.x, s.home.y, 0x40d0d8, 1.2, 600); // arrival shimmer
    this.emit('toast', { icon: '🌀', text: 'You step out at home' });
    this.emit('inv-changed');
  }
  cancelPortal(): void { this.pendingPortal = false; }

  // ---- appearance ----
  /** Set the wanderer's name (trimmed, capped) — shown on the Hero tab. */
  setName(name: string): void {
    this.save.player.name = (name ?? '').trim().slice(0, 16);
  }
  setPalette(pal: Partial<{ body: number; hair: number; outfit: number }>): void {
    const p = this.save.player.palette;
    if (pal.body != null) p.body = pal.body | 0;
    if (pal.hair != null) p.hair = pal.hair | 0;
    if (pal.outfit != null) p.outfit = pal.outfit | 0;
    bakePlayer(p);
    if (this.running && this.playerSprite) this.playerSprite.texture = tex(this.weapon === 'bow' ? 'player_bow' : 'player');
  }

  // ---- portable save code (iOS storage-purge escape hatch; wired to the settings panel) ----
  exportSave(): string { this.persist(); return exportCode(this.save); }
  /** Validate + persist an imported code. Caller reloads to boot it (avoids double-init).
   *  Stops the live sim so the reload's unload-flush can't clobber the freshly-written code. */
  importSave(code: string): boolean {
    const sv = importCode(code);
    if (!sv) return false;
    const status = writeSave(sv);
    if (status === 'ok' || status === 'soft') { this.running = false; return true; }
    return false;
  }

  /** Recompute zoom (mobile vs desktop) when the viewport changes — not just once at start. */
  onResize(): void {
    if (!this.running) return;
    this.zoom = Math.min(window.innerWidth, window.innerHeight) < MOBILE_MAX_EDGE ? ZOOM_MOBILE : ZOOM_DESKTOP;
    this.streamChunks(); // visible chunk coverage depends on zoom
  }

  // ---- lifecycle ----
  start(save: SaveData): void {
    this.save = save;
    this.zoom = Math.min(window.innerWidth, window.innerHeight) < MOBILE_MAX_EDGE ? ZOOM_MOBILE : ZOOM_DESKTOP;
    bakePlayer(this.save.player.palette); // re-skin the figure from the saved palette
    this.playerSprite = new Sprite(tex('player'));
    this.playerSprite.anchor.set(0.5, 0.7);
    this.actorLayer.addChild(this.playerSprite);
    if (!this.bowSprite) {
      this.bowSprite = new Sprite(tex('bow_held'));
      this.bowSprite.anchor.set(0.5); this.bowSprite.visible = false;
      this.actorLayer.addChild(this.bowSprite);
      this.meleeSprite = new Sprite(tex('sword_held'));
      this.meleeSprite.anchor.set(0.5); this.meleeSprite.visible = false;
      this.actorLayer.addChild(this.meleeSprite);
    }
    this.running = true;
    this.mode = 'overworld'; // dungeons are never persisted — a load is always the overworld
    this.refreshLegendaries();
    accrueBlessing(this.save.blessing, Date.now()); // bank a daily blessing for days elapsed
    this.rebuildWalls();
    // safety net: never load stuck inside a solid tile (a lake, walled-in) — nudge to walkable ground
    if (this.solidAtWorld(this.save.player.x, this.save.player.y)) {
      const w = this.findWalkable(this.save.player.x, this.save.player.y);
      this.save.player.x = w.x; this.save.player.y = w.y;
    }
    this.buildSafeZoneRing();
    this.save.world.placed.forEach((p) => this.spawnPlacedSprite(p));
    this.collectEmbers();
    this.streamChunks();
    this.initMilestones();
    this.applyGroveFilter(); // reflect any restoration already earned
    this.emit('inv-changed');
    this.emit('hud-tick');
  }

  newWorld(seed?: number, nowMs = Date.now()): void {
    const s = (seed ?? Math.random() * 0xffffffff) >>> 0;
    const spawn = findSpawn(s);
    this.save = freshSave(s, tileToWorld(spawn.tx) + TILE / 2, tileToWorld(spawn.ty) + TILE / 2, nowMs);
    if (this.running) { this.clearWorldView(); this.buildSafeZoneRing(); this.streamChunks(); this.initMilestones(); this.emit('inv-changed'); this.emit('hud-tick'); }
  }

  private clearWorldView(): void {
    // chunk + corruption sprites own UNIQUE generated textures → free them (texture:true) or they
    // leak GPU memory as you roam. Node sprites use the SHARED registry (never free their texture),
    // but destroy children to free the entrance label Text.
    this.chunkSprites.forEach((s) => s.destroy({ texture: true })); this.chunkSprites.clear(); this.chunks.clear();
    this.nodeSprites.forEach((n) => { n.sprite.destroy(); n.label?.destroy(); n.glow?.destroy(); }); this.nodeSprites.clear();
    this.corruptionGfx.forEach((s) => s.destroy({ texture: true })); this.corruptionGfx.clear();
    this.placedSprites.forEach((arr) => arr.forEach((s) => s.destroy())); this.placedSprites.clear();
    this.walls.clear();
    this.teardownFloorSprites();
    this.clearActors();
    // reset dungeon runtime for a fresh world
    this.mode = 'overworld'; this.dunFloor = null; this.pendingBlessing = false;
    this.runBuffs = { atk: 0, maxHp: 0, spdPct: 0 }; this.bossEid = 0;
    this.overGround.visible = true; this.overEnv.visible = true;
  }

  // ---- frame ----
  tick(dtReal: number): void {
    if (!this.running) return;
    if (this.hitstopLeft > 0) { this.hitstopLeft -= dtReal; this.render(); this.trackFps(dtReal); return; }
    const dt = Math.min(dtReal, 0.1);
    this.acc += dt;
    let steps = 0;
    while (this.acc >= this.STEP && steps < 8) { this.step(this.STEP); this.acc -= this.STEP; steps++; }
    this.render();
    this.trackFps(dtReal);
  }

  step(dt: number): void {
    const s = this.save;
    s.clock.gameS += dt;
    const stats = this.stats();

    // 1-2 movement + facing + collision
    const { x: mx, y: my } = this.intent.move;
    const mlen = Math.hypot(mx, my);
    if (mlen > 0.01) { this.facing = { x: mx / mlen, y: my / mlen }; }
    const speed = PLAYER_SPEED * (1 + stats.spdPct / 100);
    if (mlen > 0.01) {
      const nx = s.player.x + (mx / mlen) * speed * dt;
      const ny = s.player.y + (my / mlen) * speed * dt;
      if (!this.solidAtWorld(nx, s.player.y)) s.player.x = nx;
      if (!this.solidAtWorld(s.player.x, ny)) s.player.y = ny;
    }

    // dungeon spike traps — contact damage (player iframes throttle it to a chunk per ~second)
    if (this.mode === 'dungeon' && this.dunTraps.size
      && this.dunTraps.has(`${worldToTile(s.player.x)},${worldToTile(s.player.y)}`)) {
      this.damagePlayer(8 + this.floor * 2, 0, 0, stats);
    }

    // food + regen
    const food = resolveFood(s.buffs.food, s.clock.gameS, MAX_FOOD_BUFFS);
    s.buffs.food = food.active;
    if (food.active.length > 0 && s.player.hp < stats.maxHp) s.player.hp = Math.min(stats.maxHp, s.player.hp + REGEN_PER_S * dt);
    if (this.relic.regenPerS > 0 && s.player.hp < stats.maxHp) s.player.hp = Math.min(stats.maxHp, s.player.hp + this.relic.regenPerS * dt); // Verdant Heart relic
    // grove restoration: a small permanent regen that grows with each lit totem
    const groveRegen = s.grove.stage * GROVE_REGEN_PER_STAGE;
    if (groveRegen > 0 && s.player.hp < stats.maxHp) s.player.hp = Math.min(stats.maxHp, s.player.hp + groveRegen * dt);
    // campfire: heal aura + keep you Warmed while nearby (overworld only)
    if (this.mode === 'overworld' && this.nearCampfire()) {
      if (s.player.hp < stats.maxHp) s.player.hp = Math.min(stats.maxHp, s.player.hp + CAMPFIRE_HEAL_PER_S * dt);
      s.buffs.warmedUntilGameS = s.clock.gameS + this.warmedDuration();
    }
    if (s.player.hp > stats.maxHp) s.player.hp = stats.maxHp;

    // 3-4 AI + mob contact damage + projectiles
    this.aiTick(dt, stats);
    this.projectileTick(dt);
    // 5 pickups
    this.pickupTick();
    // 6 timers: node respawns
    for (const [key, respawnAt] of Object.entries(s.world.harvested)) if (s.clock.gameS >= respawnAt) delete s.world.harvested[key];
    // 7-8 overworld-only: ambient spawns + chunk streaming
    if (this.mode === 'overworld') { this.spawnTick(dt); this.streamChunks(); }
    this.checkMilestones();
    this.maybeAutosave();
  }

  private stats(): Stats {
    const s = this.save;
    const food = resolveFood(s.buffs.food, s.clock.gameS, MAX_FOOD_BUFFS);
    const warmed = s.clock.gameS < s.buffs.warmedUntilGameS;
    const affix = equippedAffixSum(s.gear, s.equipped);
    const eqWeapon = getEquipped(s.gear, s.equipped, 'weapon');
    const eqSub = getEquipped(s.gear, s.equipped, 'subweapon');
    const eqArmor = getEquipped(s.gear, s.equipped, 'armor');
    const eqCloak = getEquipped(s.gear, s.equipped, 'cloak');
    // key 1 wields the weapon slot (melee, fists if empty); key 2 wields the sub-weapon slot (bow).
    // Base atk comes from the wielded slot only; affixes from ALL six slots always apply.
    const primaryAtk = this.weapon === 'bow'
      ? (eqSub ? empoweredBase(eqSub).atk ?? 0 : 0)
      : (eqWeapon ? empoweredBase(eqWeapon).atk ?? 0 : FISTS_ATK);
    const armorDef = (eqArmor ? empoweredBase(eqArmor).def ?? 0 : 0) + (eqCloak ? empoweredBase(eqCloak).def ?? 0 : 0);
    const nightStride = this.equippedLegendaries.has('night_stride') && this.timeOfDay().isNight ? 12 : 0;
    return computeStats({
      weaponAtk: primaryAtk + affix.atk + this.runBuffs.atk,
      weaponDef: armorDef + affix.def,
      foodMaxHp: food.maxHpBonus + affix.maxHp + this.motif.hpFlat + this.runBuffs.maxHp + s.grove.stage * GROVE_MAXHP_PER_STAGE,
      foodSpdPct: food.spdPct + affix.spdPct + nightStride + this.runBuffs.spdPct + this.relic.spdPct,
      warmedSpdPct: warmed ? WARMED_SPD_PCT : 0,
      combatXp: s.skills.combat,
      critBonus: affix.critPct,
    });
  }

  private mintUid(): string { return `g${Date.now().toString(36)}${(this.eid++).toString(36)}`; }
  private refreshLegendaries(): void {
    this.equippedLegendaries.clear();
    for (const slot of SLOTS) { const g = getEquipped(this.save.gear, this.save.equipped, slot); if (g?.legendary) this.equippedLegendaries.add(g.legendary); }
    this.motif = motifEffects(this.save.gear, this.save.equipped);
    this.relic = relicEffects(this.save.relics, this.save.equippedRelics);
  }
  private blessingActive(id: string): boolean {
    const b = this.save.blessing;
    return b.claimedDayUTC === dayUTC(Date.now()) && b.activeId === id;
  }

  // ---- equipment & salvage ----
  equip(uid: string): boolean {
    const s = this.save;
    // gear may live in gear[] or be a satchel item — equipping moves it into gear[] (worn = banked)
    let g = s.gear.find((x) => x.uid === uid);
    if (!g) {
      const si = s.satchel.gear.findIndex((x) => x.uid === uid);
      if (si < 0) return false;
      g = s.satchel.gear.splice(si, 1)[0]; s.gear.push(g);
    }
    s.equipped[g.slot] = uid;
    this.refreshLegendaries();
    this.emit('equip'); this.emit('inv-changed'); this.emit('stats-changed');
    return true;
  }
  unequip(slot: Slot): void {
    delete this.save.equipped[slot];
    if (slot === 'subweapon' && this.weapon === 'bow') this.weapon = 'melee'; // can't wield a bow you removed
    this.refreshLegendaries();
    this.emit('inv-changed'); this.emit('stats-changed');
  }
  /** Wear a relic in slot 0 or 1 (two relic slots; a relic can only sit in one). */
  equipRelic(uid: string, slotIndex: number): boolean {
    const s = this.save;
    if (slotIndex !== 0 && slotIndex !== 1) return false;
    if (!s.relics.some((x) => x.uid === uid)) return false;
    const other = slotIndex === 0 ? 1 : 0;
    if (s.equippedRelics[other] === uid) s.equippedRelics[other] = null;
    s.equippedRelics[slotIndex] = uid;
    this.refreshLegendaries();
    this.emit('equip'); this.emit('inv-changed'); this.emit('stats-changed');
    return true;
  }
  unequipRelic(slotIndex: number): void {
    if (slotIndex !== 0 && slotIndex !== 1) return;
    this.save.equippedRelics[slotIndex] = null;
    this.refreshLegendaries();
    this.emit('inv-changed'); this.emit('stats-changed');
  }
  /** Salvage an unworn relic for gold (clears clutter from the collection). */
  salvageRelic(uid: string): number {
    const s = this.save;
    if (s.equippedRelics.includes(uid)) { this.emit('toast', { icon: '⚠️', text: 'Unequip it first' }); return 0; }
    const i = s.relics.findIndex((r) => r.uid === uid);
    if (i < 0) return 0;
    const r = s.relics[i];
    const gold = 5 + r.rarity * 8 + r.floor * 2;
    s.relics.splice(i, 1);
    s.player.gold += gold;
    this.emit('toast', { icon: '♻️', text: `Salvaged ${r.name} for ${gold} gold` });
    this.emit('inv-changed');
    return gold;
  }
  salvageBelow(rarity: Rarity): number {
    const s = this.save;
    let gold = 0;
    const equippedUids = new Set(Object.values(s.equipped));
    s.gear = s.gear.filter((g) => {
      if (g.rarity < rarity && !g.crafted && !equippedUids.has(g.uid)) { gold += salvageValue(g); return false; } // keep crafted baseline
      return true;
    });
    s.player.gold += gold;
    this.emit('toast', { icon: '♻️', text: `Salvaged for ${gold} gold` });
    this.emit('inv-changed');
    return gold;
  }
  empower(uid: string): boolean {
    const s = this.save;
    const g = s.gear.find((x) => x.uid === uid); if (!g) return false;
    if (g.empower.level >= EMPOWER_MAX) { this.emit('toast', { icon: '⚒️', text: 'Already fully empowered' }); return false; }
    const cost = empowerCost(g.empower.level);
    if (s.player.gold < cost) { this.emit('toast', { icon: '🪙', text: `Need ${cost} gold to empower` }); return false; }
    s.player.gold -= cost;
    g.empower.level++;
    // every 2nd level: one EXISTING affix bumps — single-layer reveal, no fail chance
    if (g.empower.level % 2 === 0 && g.affixes.length) {
      const a = g.affixes[this.lootRng.int(g.affixes.length)];
      g.empower.bumps[a.id] = (g.empower.bumps[a.id] ?? 0) + EMPOWER_AFFIX_STEP;
      this.emit('toast', { icon: '✨', text: `${g.name}: +${EMPOWER_AFFIX_STEP} ${a.id} (+${g.empower.level})` });
    } else {
      this.emit('toast', { icon: '⚒️', text: `${g.name} empowered to +${g.empower.level}` });
    }
    this.refreshLegendaries();
    this.emit('inv-changed'); this.emit('stats-changed');
    return true;
  }
  setTier(n: number): void {
    this.save.dungeon.tier = Math.max(1, Math.min(TIER_MAX, Math.floor(n)));
    this.emit('inv-changed');
  }
  claimBlessing(): boolean {
    const b = this.save.blessing;
    const today = dayUTC(Date.now());
    if (b.claimedDayUTC === today) { this.emit('toast', { icon: '🌸', text: 'Already blessed today' }); return false; }
    if (b.banked <= 0) { this.emit('toast', { icon: '🌸', text: 'No blessing banked yet' }); return false; }
    b.banked--; b.claimedDayUTC = today; b.activeId = todaysBlessing(Date.now()).id;
    this.emit('toast', { icon: '🌸', text: `Grove Blessing: ${todaysBlessing(Date.now()).label}` });
    this.emit('inv-changed');
    return true;
  }

  private addGear(g: GearItem): void {
    g.uid = this.mintUid();
    if (this.mode === 'dungeon') { this.save.satchel.gear.push(g); this.emit('toast', { icon: '🎁', text: `Satchel: ${g.name}` }); }
    else { this.save.gear.push(g); this.emit('toast', { icon: '🎁', text: `Found ${g.name}` }); }
    this.emit('loot', { rarity: g.rarity, legendary: !!g.legendary, x: this.save.player.x, y: this.save.player.y });
    // rare+ drops sparkle at the player's feet; legendaries erupt in a gold burst + a shake
    if (g.rarity >= 3) {
      const px = this.save.player.x, py = this.save.player.y;
      const color = g.legendary ? 0xf0a030 : 0xb060e0;
      this.spawnPop(px, py, color, g.legendary ? 1.7 : 1.2, g.legendary ? 850 : 600);
      if (g.legendary) {
        // a legendary is the rarest thrill — a double ring bursting outward + a firmer shake
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          this.spawnPop(px + Math.cos(a) * 24, py + Math.sin(a) * 18, color, 0.85, 720 + i * 30);
          this.spawnPop(px + Math.cos(a) * 44, py + Math.sin(a) * 32, 0xfff0c0, 0.55, 820 + i * 30);
        }
        this.addShake(7);
      }
    }
    this.emit('inv-changed');
  }
  private rollFloorGear(minRarity?: Rarity): GearItem {
    const eff = this.floor + (this.save.dungeon.tier - 1) * TIER_LOOT_SHIFT; // difficulty tier boosts loot
    return rollGear({ floor: eff, rng: this.lootRng, equipped: { atkIds: equippedAffixIds(this.save.gear, this.save.equipped) }, minRarity });
  }
  // Relics are owned the moment they drop (they don't ride the at-risk satchel) — rare dungeon passives.
  private addRelic(minRarity?: Rarity): void {
    const eff = this.floor + (this.save.dungeon.tier - 1) * TIER_LOOT_SHIFT;
    const r = rollRelic(eff, this.lootRng);
    if (minRarity != null && r.rarity < minRarity) r.rarity = minRarity;
    r.uid = this.mintUid();
    this.save.relics.push(r);
    this.recordCodex('relics', r.effect);
    this.emit('toast', { icon: RELIC_ICON[r.effect], text: `Relic found: ${r.name}` });
    this.spawnPop(this.save.player.x, this.save.player.y, 0x9ff0d0, 1.2, 640);
    this.emit('loot', { rarity: r.rarity, legendary: false, x: this.save.player.x, y: this.save.player.y });
    this.emit('inv-changed');
  }

  // ---- player attack (melee arc or bow) ----
  tryAttack(): void {
    if (!this.running) return;
    const s = this.save;
    if (s.clock.gameS < this.attackCdUntil) return;
    const st = this.stats();
    this.emit('attack', { weapon: this.weapon });
    if (this.weapon === 'bow') {
      this.attackCdUntil = s.clock.gameS + BOW_CD;
      const target = this.nearestMob(BOW_RANGE);
      let dir = { ...this.facing };
      if (target) { const dx = target.mob.x - s.player.x, dy = target.mob.y - s.player.y, d = Math.hypot(dx, dy) || 1; dir = { x: dx / d, y: dy / d }; }
      this.spawnProjectile(dir, st.atk);
      return;
    }
    this.attackCdUntil = s.clock.gameS + ATTACK_CD;
    this.spawnSwingFx();
    for (const inst of this.mobs.values()) {
      const dx = inst.mob.x - s.player.x, dy = inst.mob.y - s.player.y;
      const d = Math.hypot(dx, dy);
      if (d < 0.001 || d > ATTACK_ARC_RANGE) continue;
      const dot = (dx / d) * this.facing.x + (dy / d) * this.facing.y;
      if (d >= POINT_BLANK && dot < ATTACK_ARC_DOT) continue; // point-blank always connects
      this.hitMob(inst, st, dx / d, dy / d);
    }
  }

  private hitMob(inst: MobInst, st: Stats, nx: number, ny: number, melee = true): void {
    const hit = computeHit(st.atk, st.critPct, this.combatRng);
    const dmg = Math.round(hit.dmg), crit = hit.crit; // integer damage (no 16.2000003 floats)
    inst.mob.hp -= dmg;
    if (melee && this.motif.lifestealPct > 0) this.save.player.hp = Math.min(st.maxHp, this.save.player.hp + (dmg * this.motif.lifestealPct) / 100);
    inst.mob.provoked = true;
    inst.mob.kbX += nx * KNOCKBACK; inst.mob.kbY += ny * KNOCKBACK;
    inst.mob.hitFlashUntilGameS = this.save.clock.gameS + 0.08;
    this.spawnDamageNumber(inst.mob.x, inst.mob.y - 20, dmg, crit);
    this.emit('hit', { crit });
    this.hitstopLeft = Math.max(this.hitstopLeft, crit ? HITSTOP * 1.6 : HITSTOP);
    this.addShake(crit ? 5 : 2.5);
    if (crit) this.spawnPop(inst.mob.x, inst.mob.y - 10, 0xffd23f, 0.55, 260); // gold crit spark
    if (crit) this.dispatchHooks({ t: 'crit', eid: inst.mob.eid, targetX: inst.mob.x, targetY: inst.mob.y, dmg });
    // pack link: hitting one wolf provokes nearby kin
    const def = MOBS[inst.mob.defId];
    if (def.packLink) {
      for (const other of this.mobs.values()) {
        if (other.mob.defId === def.id && Math.hypot(other.mob.x - inst.mob.x, other.mob.y - inst.mob.y) < 160) other.mob.provoked = true;
      }
    }
    if (inst.mob.hp <= 0) this.killMob(inst);
  }

  /** Record a first-time collection entry (creature defeated, recipe crafted, relic effect found). */
  private recordCodex(kind: 'mobs' | 'recipes' | 'relics', id: string): void {
    const list = this.save.codex[kind];
    if (!list.includes(id)) { list.push(id); this.emit('inv-changed'); }
  }

  private killMob(inst: MobInst): void {
    const def = MOBS[inst.mob.defId];
    this.recordCodex('mobs', inst.mob.defId);
    for (const d of rollDrops(def, this.spawnRng)) this.spawnDrop(inst.mob.x, inst.mob.y, d.item, d.n);
    // death burst — a satisfying puff; bosses erupt in a golden ring + a shake
    const dx0 = inst.mob.x, dy0 = inst.mob.y;
    this.spawnPop(dx0, dy0, 0xe6ddcb, inst.mob.isBoss ? 2.0 : 0.75, inst.mob.isBoss ? 750 : 320);
    if (inst.mob.isBoss) {
      for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; this.spawnPop(dx0 + Math.cos(a) * 30, dy0 + Math.sin(a) * 24, 0xffd23f, 0.85, 620 + i * 45); }
      this.addShake(6);
    }
    this.emit('kill', { boss: inst.mob.isBoss });
    this.grantXp('combat', def.xp);
    this.dispatchHooks({ t: 'kill', eid: inst.mob.eid });
    if (inst.mob.isBoss) this.onBossKilled();
    else if (this.mode === 'dungeon' && this.lootRng.float() < MOB_GEAR_CHANCE) this.addGear(this.rollFloorGear());
    else if (this.mode === 'overworld' && OVERWORLD_GEAR_MOBS.includes(inst.mob.defId) && this.lootRng.float() < OVERWORLD_GEAR_CHANCE) {
      // tough/rare overworld creatures: a shallow-floor roll (skews Common/Uncommon), owned immediately
      this.addGear(rollGear({ floor: OVERWORLD_GEAR_FLOOR, rng: this.lootRng, equipped: { atkIds: equippedAffixIds(this.save.gear, this.save.equipped) } }));
    }
    inst.sprite.destroy(); inst.label.destroy({ children: true }); inst.aura?.destroy();
    this.mobs.delete(inst.mob.eid);
  }

  // ---- AI ----
  private aiTick(dt: number, stats: Stats): void {
    const s = this.save;
    const px = s.player.x, py = s.player.y;
    for (const inst of this.mobs.values()) {
      const m = inst.mob;
      const def = MOBS[m.defId];
      const dx = px - m.x, dy = py - m.y;
      const dist = Math.hypot(dx, dy);
      // decay knockback (wall-aware)
      this.moveEntity(m, m.kbX * dt, m.kbY * dt);
      m.kbX *= 0.82; m.kbY *= 0.82;
      // despawn far — overworld ambient only; dungeon mobs belong to the floor and must persist
      if (this.mode === 'overworld' && dist > DESPAWN_DIST) { inst.sprite.destroy(); inst.label.destroy({ children: true }); inst.aura?.destroy(); this.mobs.delete(m.eid); continue; }
      // the home sanctuary vaporizes any creature that wanders inside — no loot, no XP, no gear
      if (this.inSanctuary(m.x, m.y)) {
        this.spawnPop(m.x, m.y, 0x9ff0a0, 0.85, 340); // sanctuary shimmer where it was banished
        inst.sprite.destroy(); inst.label.destroy({ children: true }); inst.aura?.destroy();
        this.mobs.delete(m.eid);
        continue;
      }

      m.state = nextState({
        def, distToPlayer: dist, provoked: m.provoked,
        attackCdReady: s.clock.gameS >= m.attackCdUntilGameS,
        windupDone: s.clock.gameS >= m.windupUntilGameS,
      });

      if (m.state === 'wander') {
        if (s.clock.gameS >= m.wanderUntilGameS) {
          m.wanderUntilGameS = s.clock.gameS + 1 + this.spawnRng.float() * 2;
          const a = this.spawnRng.float() * Math.PI * 2;
          m.facing = { x: Math.cos(a), y: Math.sin(a) };
        }
        this.moveEntity(m, m.facing.x * def.speed * 0.3 * dt, m.facing.y * def.speed * 0.3 * dt);
      } else if (m.state === 'chase') {
        const inv = 1 / (dist || 1);
        m.facing = { x: dx * inv, y: dy * inv };
        this.moveEntity(m, m.facing.x * def.speed * dt, m.facing.y * def.speed * dt);
        m.windupUntilGameS = 0;
      } else if (m.state === 'windup') {
        if (m.windupUntilGameS === 0) m.windupUntilGameS = s.clock.gameS + (m.isBoss ? BOSS_WINDUP : MOB_WINDUP);
      } else if (m.state === 'attack') {
        m.attackCdUntilGameS = s.clock.gameS + MOB_ATTACK_CD;
        m.windupUntilGameS = 0;
        this.damagePlayer(def.dmg * m.dmgScale, dx, dy, stats);
      }
    }
  }

  private damagePlayer(dmg: number, fromDx: number, fromDy: number, stats: Stats): void {
    const s = this.save;
    if (s.clock.gameS < this.iframesUntil) return;
    if (this.inSafeZone()) return; // home sanctuary — no damage lands (the ring shows the bounds)
    if (this.relic.dodgePct > 0 && this.combatRng.float() * 100 < this.relic.dodgePct) {
      this.iframesUntil = s.clock.gameS + PLAYER_IFRAMES; // Wind Charm relic: the blow whiffs
      this.spawnPop(s.player.x, s.player.y - 20, 0x9ff0d0, 0.9, 320);
      return;
    }
    s.player.hp -= dmg;
    this.iframesUntil = s.clock.gameS + PLAYER_IFRAMES;
    this.playerHurtUntil = s.clock.gameS + 0.15;
    this.addShake(6);
    this.spawnDamageNumber(s.player.x, s.player.y - 24, dmg, false, 0xff5555);
    this.emit('player-hit');
    if (s.player.hp <= 0) { if (this.mode === 'dungeon') this.dungeonDeath(); else this.playerDeath(); }
  }

  private playerDeath(): void {
    const s = this.save;
    const lost = Math.floor(s.player.gold * RESPAWN_GOLD_LOSS);
    s.player.gold -= lost;
    s.player.x = s.home.x; s.player.y = s.home.y;
    const st = this.stats();
    s.player.hp = st.maxHp;
    this.iframesUntil = s.clock.gameS + PLAYER_IFRAMES;
    // clear nearby mobs so you don't chain-die
    for (const inst of this.mobs.values()) { inst.sprite.destroy(); inst.label.destroy({ children: true }); inst.aura?.destroy(); }
    this.mobs.clear();
    this.emit('toast', { icon: '💀', text: lost > 0 ? `You died — lost ${lost} gold` : 'You died' });
    this.emit('player-hit');
    this.emit('player-died', { place: 'wild', lostGold: lost });
  }

  // ---- projectiles ----
  private spawnProjectile(dir: { x: number; y: number }, dmg: number): void {
    const eid = this.eid++;
    const proj: Projectile = {
      eid, x: this.save.player.x, y: this.save.player.y,
      vx: dir.x * BOW_SPEED, vy: dir.y * BOW_SPEED, dmg,
      diesAtGameS: this.save.clock.gameS + BOW_RANGE / BOW_SPEED,
    };
    const sprite = new Sprite(tex('arrow'));
    sprite.anchor.set(0.5);
    sprite.rotation = Math.atan2(dir.y, dir.x);
    this.actorLayer.addChild(sprite);
    this.projectiles.set(eid, { proj, sprite });
  }

  private projectileTick(dt: number): void {
    const s = this.save;
    for (const inst of this.projectiles.values()) {
      const p = inst.proj;
      p.x += p.vx * dt; p.y += p.vy * dt;
      if (s.clock.gameS >= p.diesAtGameS) { inst.sprite.destroy(); this.projectiles.delete(p.eid); continue; }
      // walls stop arrows — no shooting mobs through a wall into the next room. At BOW_SPEED the
      // per-step move (~7px) is far under a 32px tile, so a point check can't tunnel a 1-tile wall.
      if (this.solidAtWorld(p.x, p.y)) { this.spawnPop(p.x, p.y, 0xcfc0a0, 0.35, 150); inst.sprite.destroy(); this.projectiles.delete(p.eid); continue; }
      for (const m of this.mobs.values()) {
        if (Math.hypot(m.mob.x - p.x, m.mob.y - p.y) < 16) {
          const dd = Math.hypot(p.vx, p.vy) || 1;
          this.hitMob(m, this.stats(), p.vx / dd, p.vy / dd, false); // arrow — no lifesteal
          inst.sprite.destroy(); this.projectiles.delete(p.eid);
          break;
        }
      }
    }
  }

  // ---- drops + pickup ----
  private spawnDrop(x: number, y: number, item: ItemId | 'gold', n: number): void {
    const eid = this.eid++;
    const drop: GroundDrop = { eid, x: x + (this.spawnRng.float() - 0.5) * 16, y: y + (this.spawnRng.float() - 0.5) * 16, bornAtGameS: this.save.clock.gameS, item, n };
    const sprite = new Sprite(tex(item === 'gold' ? 'drop_gold' : 'drop_item'));
    sprite.anchor.set(0.5);
    this.actorLayer.addChild(sprite);
    this.drops.set(eid, { drop, sprite });
  }

  private pickupTick(): void {
    const s = this.save;
    for (const inst of this.drops.values()) {
      const d = inst.drop;
      if (Math.hypot(d.x - s.player.x, d.y - s.player.y) < 22) {
        if (this.mode === 'dungeon') {
          // dungeon loot rides the satchel (at 30% risk on death)
          if (d.item === 'gold') s.satchel.gold += d.n;
          else s.satchel.items[d.item] = (s.satchel.items[d.item] ?? 0) + d.n;
          this.emit('toast', { icon: d.item === 'gold' ? '🪙' : ITEMS[d.item]?.icon, text: `Satchel +${d.n}` });
        } else if (d.item === 'gold') { const g = Math.round(d.n * this.goldMult()); s.player.gold += g; this.emit('toast', { icon: '🪙', text: `+${g} gold` }); }
        else this.addItem(d.item, d.n);
        this.emit('pickup', { item: d.item, x: d.x, y: d.y });
        this.spawnPop(d.x, d.y, d.item === 'gold' ? 0xf0d040 : 0xbfe0a0, 0.5, 260);
        inst.sprite.destroy(); this.drops.delete(d.eid);
      } else if (s.clock.gameS - d.bornAtGameS > 120) { inst.sprite.destroy(); this.drops.delete(d.eid); }
    }
  }

  // ---- spawning ----
  private spawnTick(dt: number): void {
    if (!this.ambientSpawns) return;
    this.spawnAccum += dt;
    if (this.spawnAccum < SPAWN_INTERVAL) return;
    this.spawnAccum = 0;
    if (this.mobs.size >= MOB_CAP) return;
    const s = this.save;
    const a = this.spawnRng.float() * Math.PI * 2;
    const r = SPAWN_MIN_DIST + this.spawnRng.float() * (SPAWN_MAX_DIST - SPAWN_MIN_DIST);
    const x = s.player.x + Math.cos(a) * r;
    const y = s.player.y + Math.sin(a) * r;
    if (this.solidAtWorld(x, y)) return;
    if (this.inSanctuary(x, y)) return; // nothing spawns in the sanctuary
    const biome = biomeAt(s.seed, worldToTile(x), worldToTile(y));
    const isNight = this.timeOfDay().isNight;
    const corrupt = corruptionAt(s.seed, x, y, CORRUPTION_RADIUS);
    const defId = spawnCandidate(biome, isNight, corrupt, this.spawnRng);
    if (!defId) return;
    this.spawnMob(defId, x, y);
  }

  teleport(tx: number, ty: number): void {
    this.save.player.x = tileToWorld(tx) + TILE / 2;
    this.save.player.y = tileToWorld(ty) + TILE / 2;
    this.streamChunks();
  }
  setGold(n: number): void { this.save.player.gold = n; }

  spawnMob(defId: string, x: number, y: number, scale = 1, isBoss = false, bossKind?: BossKind): void {
    const def = MOBS[defId];
    if (!def) return;
    // boss tiers: mini is a speed bump, the final Dungeon Boss is a wall — vary both power and size
    const kindMult = bossKind === 'final' ? 1.7 : bossKind === 'big' ? 1.15 : bossKind === 'mini' ? 0.7 : 1;
    const eff = scale * kindMult;
    const eid = this.eid++;
    const mob: Mob = {
      eid, defId, x, y, hp: def.hp * eff, maxHp: def.hp * eff, state: 'wander', provoked: isBoss,
      facing: { x: 0, y: 1 }, wanderUntilGameS: 0, windupUntilGameS: 0,
      attackCdUntilGameS: 0, hitFlashUntilGameS: 0, kbX: 0, kbY: 0,
      dmgScale: eff, isBoss,
    };
    // elite (gear-dropping) overworld creatures wear a soft amber aura so players can spot them;
    // the final Dungeon Boss wears a bigger violet one so the endgame fight reads as special.
    let aura: Sprite | undefined;
    if (bossKind === 'final') {
      aura = new Sprite(tex('glow'));
      aura.anchor.set(0.5); aura.blendMode = 'add'; aura.tint = 0x9a5adf; aura.alpha = 0.55; aura.scale.set(3.6);
      this.actorLayer.addChild(aura);
    } else if (!isBoss && OVERWORLD_GEAR_MOBS.includes(defId)) {
      aura = new Sprite(tex('glow'));
      aura.anchor.set(0.5); aura.blendMode = 'add'; aura.tint = 0xffcc3a; aura.alpha = 0.5; aura.scale.set(2.1);
      this.actorLayer.addChild(aura);
    }
    // each boss tier has its own model: acolyte (mini) → bone warden (big) → hollow king (final)
    const bossTex = bossKind === 'final' ? 'dungeon_boss' : bossKind === 'big' ? 'boss_warden' : bossKind === 'mini' ? 'boss_acolyte' : defId;
    const sprite = new Sprite(tex(bossTex));
    sprite.anchor.set(0.5, 0.8);
    // final > big > mini > mob; the render loop multiplies this by the windup pulse each frame
    const baseScale = bossKind === 'final' ? 2.4 : bossKind === 'big' ? 2.0 : bossKind === 'mini' ? 1.5 : 1;
    this.actorLayer.addChild(sprite);
    const label0 = bossKind ? bossName(bossKind) : def.name;
    const { label, barFill, barW } = this.makeMobLabel(label0, isBoss);
    this.actorLayer.addChild(label);
    this.mobs.set(eid, { mob, sprite, label, barFill, barW, aura, baseScale });
  }

  /** Floating name + health bar shown above a mob: Name (top), bar (middle), animal (below). */
  private makeMobLabel(name: string, isBoss: boolean): { label: Container; barFill: Sprite; barW: number } {
    const label = new Container();
    label.zIndex = 3e9; // always above the actors it annotates
    const barW = isBoss ? 60 : 34, barH = isBoss ? 6 : 4;
    const nameText = new Text({
      text: name,
      style: { fontFamily: 'system-ui', fontSize: isBoss ? 14 : 11, fill: isBoss ? 0xffd23f : 0xf2ead9, fontWeight: '700', stroke: { color: 0x000000, width: 3 } },
    });
    nameText.anchor.set(0.5, 1); nameText.y = -barH - 3;
    const barBg = new Sprite(Texture.WHITE);
    barBg.tint = 0x000000; barBg.alpha = 0.55; barBg.anchor.set(0.5, 0.5);
    barBg.width = barW + 2; barBg.height = barH + 2;
    const barFill = new Sprite(Texture.WHITE);
    barFill.tint = 0x4caf50; barFill.anchor.set(0, 0.5); barFill.x = -barW / 2;
    barFill.width = barW; barFill.height = barH;
    label.addChild(nameText, barBg, barFill);
    return { label, barFill, barW };
  }

  // ---- crafting & building ----
  craft(recipeId: string): boolean {
    const r = recipeById(recipeId);
    if (!r) return false;
    if (!canCraft(r, this.save.inventory, this.nearCampfire())) {
      this.emit('toast', { icon: '❌', text: r.near && !this.nearCampfire() ? 'Need a campfire nearby' : 'Missing materials' });
      return false;
    }
    const s = this.save;
    // weapon/armor become a crafted GearItem — one owned per recipe (don't spend mats on a dupe)
    if ((r.out.kind === 'weapon' || r.out.kind === 'armor') && s.gear.some((g) => g.crafted === r.id)) {
      this.emit('toast', { icon: '✓', text: `${r.name} already crafted` });
      return false;
    }
    for (const [item, n] of Object.entries(r.needs)) { s.inventory[item] = (s.inventory[item] ?? 0) - (n ?? 0); if (s.inventory[item]! <= 0) delete s.inventory[item]; }
    const out = r.out;
    if (out.kind === 'item') this.addItem(out.item, out.n);
    else if (out.kind === 'tool') { s.tools[out.tool] = Math.max(s.tools[out.tool], out.tier); this.emit('toast', { icon: r.icon, text: `Crafted ${r.name}` }); }
    else { // weapon | armor → a crafted GearItem, equipped exactly like dungeon loot
      const base = makeCraftedGear(r.id);
      if (base) {
        const uid = this.mintUid();
        const g = { ...base, uid };
        s.gear.push(g);
        // auto-equip a crafted piece when it beats what's worn there (so an upgrade feels like one)
        const primary = (x: GearItem) => (x.slot === 'armor' || x.slot === 'cloak' ? empoweredBase(x).def ?? 0 : empoweredBase(x).atk ?? 0);
        const curUid = s.equipped[base.slot];
        const cur = curUid ? s.gear.find((x) => x.uid === curUid) : undefined;
        let worn = '';
        if (!cur || primary(g) > primary(cur)) { s.equipped[base.slot] = uid; this.refreshLegendaries(); worn = ' — equipped'; }
        this.emit('toast', { icon: r.icon, text: `Crafted ${r.name}${worn}` });
      }
    }
    this.recordCodex('recipes', r.id);
    this.grantXp('craft', 1);
    this.emit('craft');
    this.emit('inv-changed');
    return true;
  }

  /** Current sanctuary radius, grown by the save's upgrade level (capped). */
  safeRadius(): number { return SAFE_ZONE_RADIUS + this.save.sanctuary * SANCTUARY_STEP; }
  /** Gold + stone to buy the next sanctuary level; null once maxed. */
  sanctuaryCost(): { gold: number; stone: number } | null {
    const lvl = this.save.sanctuary;
    if (lvl >= SANCTUARY_MAX_LEVEL) return null;
    return { gold: SANCTUARY_BASE_GOLD * (lvl + 1), stone: SANCTUARY_BASE_STONE * (lvl + 1) };
  }
  /** Spend to widen the home sanctuary by one step, up to the cap. */
  expandSanctuary(): boolean {
    const cost = this.sanctuaryCost();
    if (!cost) { this.emit('toast', { icon: '🛡️', text: 'The sanctuary is at its widest' }); return false; }
    const s = this.save;
    if (s.player.gold < cost.gold || (s.inventory['stone'] ?? 0) < cost.stone) {
      this.emit('toast', { icon: '🛡️', text: `Need ${cost.gold} gold + ${cost.stone} stone` });
      return false;
    }
    s.player.gold -= cost.gold;
    s.inventory['stone']! -= cost.stone; if (s.inventory['stone']! <= 0) delete s.inventory['stone'];
    s.sanctuary += 1;
    this.buildSafeZoneRing();
    this.emit('toast', { icon: '🛡️', text: `Sanctuary widened (Lv ${s.sanctuary})` });
    this.emit('inv-changed');
    this.persist();
    return true;
  }

  /** The soft ground ring that marks the home sanctuary (rebuilt on start / new world / upgrade). */
  private buildSafeZoneRing(): void {
    this.safeZoneGfx?.destroy();
    const r = this.safeRadius();
    const g = new Graphics();
    g.circle(0, 0, r).fill({ color: 0x6fe07a, alpha: 0.10 });
    g.circle(0, 0, r).stroke({ width: 3, color: 0x9ff0a0, alpha: 0.55 });
    g.x = this.save.home.x; g.y = this.save.home.y; g.zIndex = -1e9; // under nodes/actors
    this.overEnv.addChild(g);
    this.safeZoneGfx = g;
  }

  /** Is a world point inside the home sanctuary? Overworld-only. */
  private inSanctuary(x: number, y: number): boolean {
    if (!this.safeZone || this.mode !== 'overworld') return false;
    return Math.hypot(x - this.save.home.x, y - this.save.home.y) <= this.safeRadius();
  }
  /** Is the PLAYER inside the home sanctuary? Danger can't touch you here. */
  inSafeZone(): boolean { return this.inSanctuary(this.save.player.x, this.save.player.y); }
  setSafeZone(on: boolean): void { this.safeZone = on; }

  nearCampfire(): boolean {
    const s = this.save;
    return s.world.placed.some((p) => p.kind === 'campfire'
      && Math.hypot(tileToWorld(p.tx) + TILE / 2 - s.player.x, tileToWorld(p.ty) + TILE / 2 - s.player.y) <= CAMPFIRE_RADIUS);
  }

  private warmedDuration(): number {
    const s = this.save;
    const comforts = s.world.placed.filter((p) =>
      Math.hypot(tileToWorld(p.tx) + TILE / 2 - s.player.x, tileToWorld(p.ty) + TILE / 2 - s.player.y) <= WARMED_COMFORT_RADIUS).length;
    return WARMED_BASE_S + comforts * WARMED_PER_COMFORT_S;
  }

  place(itemId: ItemId, tx: number, ty: number): boolean {
    const s = this.save;
    if ((s.inventory[itemId] ?? 0) <= 0) return false;
    if (!['campfire', 'torch', 'wooden_wall', 'home_portal'].includes(itemId)) return false;
    const wx = tileToWorld(tx) + TILE / 2, wy = tileToWorld(ty) + TILE / 2;
    if (Math.hypot(wx - s.player.x, wy - s.player.y) > PLACE_RANGE) { this.emit('toast', { icon: '📍', text: 'Too far to build' }); return false; }
    if (this.solidAtWorld(wx, wy)) { this.emit('toast', { icon: '🚫', text: "Can't build there" }); return false; }
    if (s.world.placed.some((p) => p.tx === tx && p.ty === ty)) return false;
    s.inventory[itemId]! -= 1; if (s.inventory[itemId]! <= 0) delete s.inventory[itemId];
    const placed: Placed = { kind: itemId as Placed['kind'], tx, ty };
    s.world.placed.push(placed);
    this.rebuildWalls();
    this.spawnPlacedSprite(placed);
    if (itemId === 'home_portal') this.ensureHomeAnchor(); // its twin lights up back home
    this.emit('inv-changed');
    this.emit('toast', { icon: ITEMS[itemId]?.icon, text: `Placed ${ITEMS[itemId]?.name}` });
    return true;
  }

  /** Build one tile in the player's facing direction (used by the UI build buttons + tests). */
  buildFacing(itemId: ItemId): boolean {
    const s = this.save;
    const tx = worldToTile(s.player.x + this.facing.x * TILE);
    const ty = worldToTile(s.player.y + this.facing.y * TILE);
    return this.place(itemId, tx, ty);
  }

  private rebuildWalls(): void {
    this.walls.clear();
    for (const p of this.save.world.placed) if (p.kind === 'wooden_wall') this.walls.add(`${p.tx},${p.ty}`);
  }

  private spawnPlacedSprite(p: Placed): void {
    const key = `${p.tx},${p.ty}`;
    if (this.placedSprites.has(key)) return;
    const arr: Sprite[] = [];
    const sp = new Sprite(tex(p.kind));
    sp.anchor.set(0.5, 0.8);
    sp.x = tileToWorld(p.tx) + TILE / 2; sp.y = tileToWorld(p.ty) + TILE / 2; sp.zIndex = sp.y;
    this.overEnv.addChild(sp); arr.push(sp);
    if (p.kind === 'campfire' || p.kind === 'torch' || p.kind === 'home_portal') {
      const glow = new Sprite(tex('glow'));
      glow.anchor.set(0.5); glow.x = sp.x; glow.y = sp.y - 4; glow.zIndex = 2; glow.alpha = 0.7;
      glow.blendMode = 'add';
      if (p.kind === 'home_portal') { glow.tint = 0x40d0d8; glow.alpha = 0.5; } // teal rift shimmer
      this.overGround.addChild(glow); arr.push(glow);
    }
    this.placedSprites.set(key, arr);
  }

  private grantXp(skill: 'wood' | 'mine' | 'combat' | 'craft', base: number): void {
    const s = this.save;
    const warmed = s.clock.gameS < s.buffs.warmedUntilGameS;
    const before = xpToLevel(s.skills[skill]);
    s.skills[skill] += base * (warmed ? WARMED_XP_MULT : 1) * (1 + this.relic.xpPct / 100); // Sage Stone relic
    const after = xpToLevel(s.skills[skill]);
    if (after > before) { this.emit('levelup', { skill, level: after }); this.emit('toast', { icon: '✨', text: `${SKILL_NAMES[skill]} level ${after}!` }); }
  }

  private collectEmbers(): void {
    const s = this.save;
    const now = Date.now();
    if (s.world.placed.some((p) => p.kind === 'campfire')) {
      const hours = Math.min(EMBER_CAP_HOURS, (now - s.embers.lastCollectMs) / 3_600_000);
      const mult = (1 + this.motif.emberPct / 100) * (this.blessingActive('double_embers') ? 2 : 1);
      const wood = Math.floor(hours * EMBER_PER_HOUR * mult);
      if (wood > 0) { this.addItem('wood', wood); this.emit('toast', { icon: '🔥', text: `Embers: +${wood} wood while away` }); }
    }
    s.embers.lastCollectMs = now;
  }

  // ---- dungeons ----
  enterDungeon(startFloor?: number): void {
    if (this.mode !== 'overworld') return;
    const cp = this.save.dungeon.checkpoints;
    const floor = startFloor ?? (cp.length ? Math.max(...cp) + 1 : 1);
    this.persist();
    this.dunReturn = { x: this.save.player.x, y: this.save.player.y };
    this.save.dungeon.insideEntranceKey = 'in';
    this.mode = 'dungeon';
    this.runCleared = false;
    this.overGround.visible = false; this.overEnv.visible = false;
    this.app.renderer.background.color = 0x0c0a14; // dark void beyond the floor
    this.runBuffs = { atk: 0, maxHp: 0, spdPct: 0 };
    this.buildFloor(floor);
  }

  private buildFloor(floor: number): void {
    this.floor = floor;
    this.save.dungeon.deepest = Math.max(this.save.dungeon.deepest, floor);
    this.teardownFloorSprites();
    this.clearActors();
    const f = genFloor(this.save.seed, floor, this.visitNonce++);
    this.dunFloor = f;
    this.dunGround.addChild(bakeDungeonSprite(f.size, f.tiles));
    for (const c of f.chests) {
      const sp = new Sprite(tex('chest')); sp.anchor.set(0.5, 0.7);
      sp.x = tileToWorld(c.tx) + TILE / 2; sp.y = tileToWorld(c.ty) + TILE / 2; sp.zIndex = sp.y;
      this.dunEnv.addChild(sp); this.chestTiles.set(`${c.tx},${c.ty}`, sp);
    }
    // stairs down — except the final floor, which bottoms out (beat the Dungeon Boss, then leave)
    if (floor < DUNGEON_MAX_FLOOR) {
      const st = new Sprite(tex('stairs')); st.anchor.set(0.5, 0.7);
      st.x = tileToWorld(f.stairs.tx) + TILE / 2; st.y = tileToWorld(f.stairs.ty) + TILE / 2; st.zIndex = st.y;
      this.dunEnv.addChild(st);
      const stLbl = new Text({ text: '⬇ Descend', style: { fontFamily: 'system-ui', fontSize: 12, fontWeight: '700', fill: 0x9ecbf0, stroke: { color: 0x0a1420, width: 4 } } });
      stLbl.anchor.set(0.5, 1); stLbl.x = st.x; stLbl.y = st.y - 24; stLbl.zIndex = st.y + 5;
      this.dunEnv.addChild(stLbl);
      this.stairsTile = { tx: f.stairs.tx, ty: f.stairs.ty };
    } else {
      this.stairsTile = { tx: -999, ty: -999 }; // no descent on the bottom floor
    }
    // spike traps — render on the floor + remember their tiles for contact damage
    this.dunTraps.clear();
    for (const tr of f.traps) {
      const sp = new Sprite(tex('spike')); sp.anchor.set(0.5, 0.5);
      sp.x = tileToWorld(tr.tx) + TILE / 2; sp.y = tileToWorld(tr.ty) + TILE / 2; sp.zIndex = -1e6; // floor-level, under actors
      this.dunEnv.addChild(sp); // dunEnv frees WITHOUT the shared 'spike' texture (unlike dunGround)
      this.dunTraps.add(`${tr.tx},${tr.ty}`);
    }
    this.save.player.x = tileToWorld(f.entry.tx) + TILE / 2;
    this.save.player.y = tileToWorld(f.entry.ty) + TILE / 2;
    const scale = mobStatScale(floor) * (1 + (this.save.dungeon.tier - 1) * TIER_MOB_MULT);
    this.bossEid = 0;
    for (const p of f.packs) for (let i = 0; i < p.count; i++) {
      this.spawnMob(p.defId, tileToWorld(p.tx) + TILE / 2 + (this.spawnRng.float() - 0.5) * 44, tileToWorld(p.ty) + TILE / 2 + (this.spawnRng.float() - 0.5) * 44, scale);
    }
    if (f.boss) {
      this.spawnMob('bone_warden', tileToWorld(f.boss.tx) + TILE / 2, tileToWorld(f.boss.ty) + TILE / 2, scale, true, bossKindForFloor(floor) ?? undefined);
      this.bossEid = this.eid - 1;
    }
    this.pendingBlessing = true;
    this.floorEnterGameS = this.save.clock.gameS; // best-time stamp
    this.emit('mode-changed');
  }

  private teardownFloorSprites(): void {
    this.dunGround.removeChildren().forEach((c) => c.destroy({ texture: true })); // baked floor texture
    this.dunEnv.removeChildren().forEach((c) => c.destroy()); // chest/stairs use the shared registry
    this.chestTiles.clear();
  }
  private clearActors(): void {
    this.mobs.forEach((m) => { m.sprite.destroy(); m.label.destroy({ children: true }); }); this.mobs.clear();
    this.drops.forEach((d) => d.sprite.destroy()); this.drops.clear();
    this.projectiles.forEach((p) => p.sprite.destroy()); this.projectiles.clear();
  }

  descend(): void {
    if (this.mode !== 'dungeon') return;
    if (this.floor >= DUNGEON_MAX_FLOOR) { this.emit('toast', { icon: '👑', text: 'This is the deepest floor — fell the Dungeon Boss, then leave to claim your clear' }); return; }
    if (this.bossEid > 0) { this.emit('toast', { icon: '💀', text: 'Defeat the boss to descend' }); return; }
    // record this floor's clear time (best-of, per floor)
    const t = Math.round((this.save.clock.gameS - this.floorEnterGameS) * 10) / 10;
    const b = this.save.dungeon.bests;
    if (b[this.floor] == null || t < b[this.floor]) b[this.floor] = t;
    this.buildFloor(this.floor + 1);
  }

  exitDungeon(): void {
    if (this.mode !== 'dungeon') return;
    const cleared = this.runCleared;
    this.resolveSatchel(1);
    this.leaveDungeon(); // returns to the overworld entrance (dunReturn)
    if (cleared) { this.flameCollapse(this.dunReturn.x, this.dunReturn.y); this.emit('toast', { icon: '🔥', text: 'The cleared dungeon collapses in flame behind you' }); }
    else this.emit('toast', { icon: '🚪', text: 'You leave the dungeon — satchel banked' });
    this.runCleared = false;
  }

  /** A staggered burst of flame at a world point — the cleared dungeon caving in. */
  private flameCollapse(x: number, y: number): void {
    for (let i = 0; i < 14; i++) {
      const ox = (this.spawnRng.float() - 0.5) * 70, oy = (this.spawnRng.float() - 0.5) * 60;
      const warm = [0xff7a1a, 0xf7d020, 0xff3a1a][this.spawnRng.int(3)];
      this.spawnPop(x + ox, y + oy, warm, 0.7 + this.spawnRng.float() * 0.9, 500 + i * 45);
    }
  }

  private dungeonDeath(): void {
    const floor = this.floor;
    this.resolveSatchel(SATCHEL_DEATH_RATE);
    this.leaveDungeon();
    // wake at HOME, not at the dungeon mouth
    this.save.player.x = this.save.home.x; this.save.player.y = this.save.home.y;
    this.save.player.hp = this.stats().maxHp;
    this.iframesUntil = this.save.clock.gameS + PLAYER_IFRAMES;
    this.streamChunks();
    const kept = Math.round(SATCHEL_DEATH_RATE * 100);
    this.emit('toast', { icon: '💀', text: `You fell in the dungeon — only ${kept}% of the satchel survived` });
    this.emit('player-died', { place: 'dungeon', floor });
  }

  private leaveDungeon(): void {
    this.teardownFloorSprites();
    this.clearActors();
    this.dunFloor = null;
    this.mode = 'overworld';
    this.pendingBlessing = false;
    this.runBuffs = { atk: 0, maxHp: 0, spdPct: 0 };
    this.overGround.visible = true; this.overEnv.visible = true;
    this.app.renderer.background.color = 0x35431c; // back to the meadow-blend backdrop
    this.save.dungeon.insideEntranceKey = undefined;
    this.save.player.x = this.dunReturn.x; this.save.player.y = this.dunReturn.y;
    this.streamChunks();
    this.emit('mode-changed'); this.emit('inv-changed');
  }

  private resolveSatchel(rate: number): void {
    const s = this.save;
    s.player.gold += Math.floor(s.satchel.gold * rate);
    for (const [item, n] of Object.entries(s.satchel.items)) {
      const got = Math.floor(n * rate);
      if (got > 0) s.inventory[item] = (s.inventory[item] ?? 0) + got;
    }
    // gear is discrete — each piece survives with probability `rate` (Bernoulli).
    // the satchel_safe blessing guarantees one extra piece survives a death.
    let guaranteed = rate < 1 && this.blessingActive('satchel_safe') ? 1 : 0;
    for (const g of s.satchel.gear) if (rate >= 1 || guaranteed-- > 0 || this.lootRng.float() < rate) s.gear.push(g);
    s.satchel = { gold: 0, items: {}, gear: [] };
  }

  private openChest(tx: number, ty: number): void {
    const key = `${tx},${ty}`;
    const sp = this.chestTiles.get(key); if (!sp) return;
    sp.destroy(); this.chestTiles.delete(key);
    const gold = Math.round((15 + Math.floor(this.spawnRng.float() * 20) + this.floor * 5) * this.goldMult());
    const ore = 2 + Math.floor(this.spawnRng.float() * 3);
    this.save.satchel.gold += gold;
    this.save.satchel.items['iron_ore'] = (this.save.satchel.items['iron_ore'] ?? 0) + ore;
    this.emit('toast', { icon: '📦', text: `Chest: +${gold} gold, +${ore} iron ore → satchel` });
    if (this.lootRng.float() < CHEST_GEAR_CHANCE) this.addGear(this.rollFloorGear());
    if (this.lootRng.float() < 0.12) this.addRelic(); // occasional relic in a chest
  }

  // test hooks
  getDungeonInfo() {
    const f = this.dunFloor;
    return {
      mode: this.mode, floor: this.floor, deepest: this.save.dungeon.deepest,
      checkpoints: [...this.save.dungeon.checkpoints], bossAlive: this.bossEid > 0,
      chests: f ? f.chests.map((c) => ({ ...c })) : [],
      traps: f ? f.traps.map((t) => ({ ...t })) : [],
      stairs: f ? { ...f.stairs } : null,
      boss: f?.boss ? { ...f.boss } : null,
    };
  }
  debugHurt(n: number): void {
    this.save.player.hp -= n;
    if (this.save.player.hp <= 0) { if (this.mode === 'dungeon') this.dungeonDeath(); else this.playerDeath(); }
  }
  /** The player's current on-screen position in CSS px (for the DOM fog to track). */
  playerScreen(): { x: number; y: number } {
    const wr = this.worldRoot;
    return { x: wr.x + this.save.player.x * wr.scale.x, y: wr.y + this.save.player.y * wr.scale.y };
  }
  debugCam(): Record<string, number> {
    const wr = this.worldRoot; const r = this.app.renderer;
    return {
      sx: Math.round(wr.x + this.save.player.x * wr.scale.x),
      sy: Math.round(wr.y + this.save.player.y * wr.scale.y),
      rw: r.width, rh: r.height, res: r.resolution, z: wr.scale.x,
      screenW: r.screen.width, screenH: r.screen.height, innerW: window.innerWidth, innerH: window.innerHeight,
    };
  }
  debugMobs(): { defId: string; state: string; dist: number; provoked: boolean; hp: number; x: number; y: number }[] {
    const s = this.save;
    return [...this.mobs.values()].map((i) => ({
      defId: i.mob.defId, state: i.mob.state, provoked: i.mob.provoked, hp: i.mob.hp,
      x: Math.round(i.mob.x), y: Math.round(i.mob.y),
      dist: Math.round(Math.hypot(i.mob.x - s.player.x, i.mob.y - s.player.y)),
    }));
  }
  debugKillNearest(): void {
    const inst = this.nearestMob(1e9);
    if (inst) this.killMob(inst);
  }
  /** Dev grant for the live save: maxed weapon/tool tiers, one legendary weapon per effect,
   *  home portals + supplies. Run via __wg.grantAll() from the browser console. */
  debugGrant(portals = 5): void {
    const s = this.save;
    s.weapons.melee = 2; s.weapons.bow = 2; // iron sword + iron bow (max crafted)
    s.tools.axe = 3; s.tools.pickaxe = 3; s.armor = 2;
    // one legendary weapon per effect, rolled so base/affixes are always valid
    const want = new Set(LEGENDARIES.map((l) => l.id));
    const got = new Set<string>();
    for (let i = 0; i < 80 && got.size < want.size; i++) {
      const g = rollGear({ floor: 12, rng: this.lootRng, equipped: { atkIds: new Set() }, slot: 'weapon', minRarity: 4 });
      if (g.legendary && !got.has(g.legendary)) { got.add(g.legendary); g.uid = this.mintUid(); s.gear.push(g); }
    }
    s.inventory['home_portal'] = (s.inventory['home_portal'] ?? 0) + portals;
    s.inventory['relic'] = (s.inventory['relic'] ?? 0) + 10;
    s.inventory['iron_bar'] = (s.inventory['iron_bar'] ?? 0) + 20;
    s.player.gold += 9999;
    this.refreshLegendaries();
    this.emit('inv-changed'); this.emit('stats-changed');
    this.persist();
    this.emit('toast', { icon: '🎁', text: `Granted ${got.size} legendary weapons + ${portals} portals` });
  }
  debugGiveGear(partial: Partial<GearItem>): string {
    const g = { slot: 'weapon', name: 'Test', rarity: 0, base: {}, affixes: [], floor: 0, empower: { level: 0, bumps: {} }, ...partial, uid: this.mintUid() } as GearItem;
    this.save.gear.push(g);
    this.emit('inv-changed');
    return g.uid;
  }
  debugGiveRelic(partial: Partial<RelicItem>): string {
    const r = { effect: 'regen', name: 'Test Relic', rarity: 1, floor: 1, ...partial, uid: this.mintUid() } as RelicItem;
    this.save.relics.push(r);
    this.emit('inv-changed');
    return r.uid;
  }

  pickBlessing(i: number): void {
    if (!this.pendingBlessing) return;
    const b = BLESSINGS[i]; if (!b) return;
    const wasFull = this.save.player.hp >= this.stats().maxHp;
    this.runBuffs.atk += b.atk; this.runBuffs.maxHp += b.maxHp; this.runBuffs.spdPct += b.spdPct;
    if (wasFull) this.save.player.hp = this.stats().maxHp; // top off: full HP stays full when max rises
    this.pendingBlessing = false;
    this.emit('mode-changed');
  }

  private onBossKilled(): void {
    this.bossEid = 0;
    const s = this.save;
    const kind = bossKindForFloor(this.floor) ?? 'mini';
    if (!s.dungeon.checkpoints.includes(this.floor)) s.dungeon.checkpoints.push(this.floor);
    if (kind === 'mini') {
      // a speed bump — regular loot, no guaranteed rarity, no grove-relic quest key, no pity
      this.addGear(this.rollFloorGear());
      if (this.lootRng.float() < 0.5) this.addRelic();
      this.emit('toast', { icon: '💀', text: `Warden Acolyte felled — floor ${this.floor} cleared.` });
    } else {
      // big + final both guarantee Epic+ gear; pity forces a Legendary within PITY_N such kills.
      const isFinal = kind === 'final';
      const forceLegendary = s.dungeon.pity >= PITY_N - 1;
      const minR: Rarity = forceLegendary ? 4 : 3; // both the Bone Warden and the King guarantee Epic+
      const g = this.rollFloorGear(minR);
      this.addGear(g);
      s.dungeon.pity = g.legendary ? 0 : s.dungeon.pity + 1;
      this.addRelic(isFinal ? 2 : 1); // a worn (equippable) relic — separate from the grove quest relic
      if (isFinal) {
        // the Hollow King is the sole guaranteed source of the GROVE relic (feeds the restoration arc)
        this.addItem('relic', 1);
        this.runCleared = true; // exit collapses the cleared dungeon in flame
        this.emit('toast', { icon: '👑', text: 'Dungeon cleared! A grove relic is yours.' });
      } else {
        // the Bone Warden is the gear checkpoint; harder tiers add a rare bonus grove relic
        if (this.lootRng.float() < (GROVE_TIER_BONUS[s.dungeon.tier] ?? 0)) {
          this.addItem('relic', 1);
          this.emit('toast', { icon: '🌳', text: 'A grove relic surfaces from the deep tier!' });
        }
        this.emit('toast', { icon: '⭐', text: `The Bone Warden falls! Floor ${this.floor} cleared.` });
      }
    }
    this.emit('dungeon-complete', { floor: this.floor, kind, cleared: kind === 'final' });
  }

  /** World colour saturation (0..1); grows toward full, vivid colour as the grove is restored. */
  groveSaturation(): number {
    const max = GROVE_TOTEM_COSTS.length;
    return GROVE_START_SAT + (1 - GROVE_START_SAT) * (this.save.grove.stage / max);
  }
  /** Drive the colour-matrix from the current restoration; detach entirely once fully restored. */
  applyGroveFilter(): void {
    const sat = this.groveSaturation();
    if (sat >= 0.999) { this.worldRoot.filters = []; return; } // fully restored → no filter cost
    this.groveFilter.saturate((sat - 1) * 1.5, false); // (sat 1 → 0 amount = normal; <1 → gentle drain)
    this.worldRoot.filters = [this.groveFilter];
  }
  /** Light the three physical totems as a coarse in-world progress bar for the whole arc. */
  private syncTotems(): void {
    const lit = Math.min(3, Math.ceil((this.save.grove.stage / GROVE_TOTEM_COSTS.length) * 3));
    this.save.totems = [lit > 0, lit > 1, lit > 2];
  }
  /** Pour every carried grove relic into the grove, lighting any totems the total now reaches. */
  private feedGrove(): void {
    const s = this.save;
    const max = GROVE_TOTEM_COSTS.length;
    if (s.grove.stage >= max) { this.emit('toast', { icon: '🌳', text: 'The grove shines whole' }); return; }
    const have = s.inventory['relic'] ?? 0;
    if (have <= 0) { this.emit('toast', { icon: '⭐', text: 'Bring a grove relic from the Hollow King (floor 10)' }); return; }
    delete s.inventory['relic'];
    s.grove.fed += have;
    // a green sparkle at the hero every time you pour relics in, stage-up or not
    const px = s.player.x, py = s.player.y;
    for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2; this.spawnPop(px + Math.cos(a) * 16, py + Math.sin(a) * 12, 0x8fe07a, 0.5, 480 + i * 45); }
    let lit = 0;
    while (s.grove.stage < max && s.grove.fed >= groveThreshold(s.grove.stage + 1)) { s.grove.stage++; lit++; }
    if (lit > 0) {
      this.syncTotems();
      this.applyGroveFilter(); // colour seeps back into the world
      this.refreshTotemGlows(); // the totems brighten
      const line = s.grove.stage >= max
        ? 'The grove blazes whole. The light it lost is returned — and the depths remember your name.'
        : GROVE_LINES[s.grove.stage - 1] ?? 'A totem awakens; colour seeps back into the world.';
      // one event carries everything the UI needs: the meter jump, the narrative, the colour restore
      this.emit('grove-stage', { stage: s.grove.stage, max, saturation: this.groveSaturation(), line });
    } else {
      const need = groveThreshold(s.grove.stage + 1) - s.grove.fed;
      this.emit('toast', { icon: '🌳', text: `The grove drinks it in — ${need} more to the next totem` });
    }
    this.emit('inv-changed'); this.emit('stats-changed');
  }

  private nearestMob(range: number): MobInst | null {
    let best: MobInst | null = null, bestD = range;
    for (const inst of this.mobs.values()) {
      const d = Math.hypot(inst.mob.x - this.save.player.x, inst.mob.y - this.save.player.y);
      if (d < bestD) { best = inst; bestD = d; }
    }
    return best;
  }

  // ---- fx ----
  private spawnDamageNumber(x: number, y: number, dmg: number, crit: boolean, color = 0xffffff): void {
    const t = new Text({ text: crit ? `${dmg}!` : `${dmg}`, style: { fontFamily: 'system-ui', fontSize: crit ? 20 : 15, fill: crit ? 0xffd23f : color, fontWeight: '700' } });
    t.anchor.set(0.5); t.x = x; t.y = y; t.zIndex = 2e9;
    this.fxLayer.addChild(t);
    this.fx.push({ g: t, diesAtReal: performance.now() + 700, vy: -34 });
  }
  private spawnSwingFx(): void {
    const sp = new Sprite(tex('swing'));
    sp.anchor.set(0, 0.5);
    sp.x = this.save.player.x; sp.y = this.save.player.y - 6;
    sp.rotation = Math.atan2(this.facing.y, this.facing.x);
    sp.zIndex = 1.5e9;
    this.fxLayer.addChild(sp);
    this.fx.push({ g: sp, diesAtReal: performance.now() + 130, vy: 0 });
  }
  private addShake(mag: number): void { this.shake.mag = Math.max(this.shake.mag, mag); this.shake.until = performance.now() + 160; }
  /** A soft additive glow that rises + fades — used for pickups and rare-drop sparkle. */
  private spawnPop(x: number, y: number, color: number, scale: number, ms: number): void {
    const sp = new Sprite(tex('glow'));
    sp.anchor.set(0.5); sp.x = x; sp.y = y - 8; sp.zIndex = 2e9;
    sp.tint = color; sp.scale.set(scale); sp.blendMode = 'add';
    this.fxLayer.addChild(sp);
    this.fx.push({ g: sp, diesAtReal: performance.now() + ms, vy: -18 });
  }

  // ---- legendary effect hooks ----
  private goldMult(): number {
    let m = this.equippedLegendaries.has('gold_magnet') ? 1.5 : 1;
    m += this.motif.emberPct / 100; // ember motif also enriches gold
    m += this.relic.goldPct / 100; // Gilded Acorn relic
    if (this.mode === 'dungeon' && this.blessingActive('dungeon_gold')) m += 0.5;
    return m;
  }
  private dispatchHooks(ev: { t: string; eid?: number; targetX?: number; targetY?: number; dmg?: number }): void {
    if (ev.t === 'kill' && this.equippedLegendaries.has('kill_heal')) {
      const max = this.stats().maxHp;
      this.save.player.hp = Math.min(max, this.save.player.hp + 2);
    }
    if (ev.t === 'crit' && this.equippedLegendaries.has('chain_crit') && ev.targetX != null) {
      let best: MobInst | null = null; let bestD = 90;
      for (const inst of this.mobs.values()) {
        if (inst.mob.eid === ev.eid) continue;
        const d = Math.hypot(inst.mob.x - ev.targetX, inst.mob.y - (ev.targetY ?? 0));
        if (d < bestD) { best = inst; bestD = d; }
      }
      if (best) {
        best.mob.hp -= ev.dmg ?? 0;
        best.mob.hitFlashUntilGameS = this.save.clock.gameS + 0.08;
        this.spawnDamageNumber(best.mob.x, best.mob.y - 20, ev.dmg ?? 0, false, 0x88ddff);
        if (best.mob.hp <= 0) this.killMob(best);
      }
    }
  }

  // ---- interaction: harvest ----
  tryInteract(): void {
    if (!this.running) return;
    const s = this.save;
    // in a dungeon: open a chest in range, else descend at the stairs
    if (this.mode === 'dungeon') {
      for (const [key] of this.chestTiles) {
        const [tx, ty] = key.split(',').map(Number);
        if (Math.hypot(tileToWorld(tx) + TILE / 2 - s.player.x, tileToWorld(ty) + TILE / 2 - s.player.y) <= CHEST_OPEN_RANGE) { this.openChest(tx, ty); return; }
      }
      if (Math.hypot(tileToWorld(this.stairsTile.tx) + TILE / 2 - s.player.x, tileToWorld(this.stairsTile.ty) + TILE / 2 - s.player.y) <= STAIRS_RANGE) this.descend();
      return;
    }
    // overworld: step to a home portal in range → open it and ask before travelling.
    // The arrival anchor at the home tile is passive (you emerge from it, never prompt on it).
    const htx = worldToTile(s.home.x), hty = worldToTile(s.home.y);
    for (const p of s.world.placed) {
      if (p.kind !== 'home_portal' || (p.tx === htx && p.ty === hty)) continue;
      const wx = tileToWorld(p.tx) + TILE / 2, wy = tileToWorld(p.ty) + TILE / 2;
      if (Math.hypot(wx - s.player.x, wy - s.player.y) <= PLACE_RANGE) {
        this.pendingPortal = true;
        this.emit('portal-prompt'); // UI shows the "enter the portal?" confirm
        return;
      }
    }
    // overworld: nearest interactable node (entrance / totem / harvestable)
    let best: NodeSprite | null = null; let bestD = Infinity;
    for (const ns of this.nodeSprites.values()) {
      const wx = tileToWorld(ns.node.tx) + TILE / 2, wy = tileToWorld(ns.node.ty) + TILE / 2;
      const range = ns.node.kind === 'entrance' || ns.node.kind === 'totem' ? DUNGEON_ENTER_RANGE : HARVEST_RANGE;
      const d = Math.hypot(wx - s.player.x, wy - s.player.y);
      if (d <= range && d < bestD) { best = ns; bestD = d; }
    }
    if (!best) return;
    if (best.node.kind === 'entrance') { this.enterDungeon(); return; }
    if (best.node.kind === 'totem') { this.feedGrove(); return; }
    const req = requiredPickaxe(best.node.kind);
    if (req > 0 && s.tools.pickaxe < req) {
      this.emit('toast', { icon: '⛏️', text: `Need a ${['', '', 'stone ', 'iron '][req]}pickaxe` });
      return;
    }
    const power = gatherPower(best.node.kind, s.tools.axe, s.tools.pickaxe, s.skills.wood, s.skills.mine);
    best.hp -= power; best.sprite.scale.set(1.12);
    if (best.hp <= 0) {
      const bonus = this.equippedLegendaries.has('bounty') ? 1 : 0; // the Harvest legendary
      for (const y of nodeYield(best.node.kind)) this.addItem(y.item, y.n + bonus);
      const rr = toUnit(hash3i(s.seed, best.node.tx, best.node.ty, Math.floor(s.clock.gameS)));
      s.world.harvested[best.node.key] = s.clock.gameS + NODE_RESPAWN_MIN + rr * (NODE_RESPAWN_MAX - NODE_RESPAWN_MIN);
      best.sprite.destroy(); this.nodeSprites.delete(best.node.key);
      const wood = best.node.kind === 'tree' || best.node.kind === 'bush';
      this.emit('harvest', { wood });
      this.grantXp(wood ? 'wood' : 'mine', 1);
    }
  }

  addItem(id: ItemId, n: number): void {
    this.save.inventory[id] = (this.save.inventory[id] ?? 0) + n;
    this.emit('inv-changed');
    this.emit('toast', { icon: ITEMS[id]?.icon ?? '❔', text: `+${n} ${ITEMS[id]?.name ?? id}` });
  }

  eat(id: ItemId): boolean {
    const s = this.save;
    if ((s.inventory[id] ?? 0) <= 0) return false;
    const buff = makeFoodBuff(id, s.clock.gameS);
    if (!buff) return false;
    const wasFull = s.player.hp >= this.stats().maxHp;
    s.inventory[id]! -= 1; if (s.inventory[id]! <= 0) delete s.inventory[id];
    s.buffs.food.push(buff);
    s.buffs.food = resolveFood(s.buffs.food, s.clock.gameS, MAX_FOOD_BUFFS).active;
    if (wasFull) s.player.hp = this.stats().maxHp; // top off: full HP stays full when max rises
    this.emit('inv-changed');
    this.emit('toast', { icon: ITEMS[id]?.icon, text: `Ate ${ITEMS[id]?.name}` });
    return true;
  }

  // ---- streaming (viewport-based) ----
  private streamChunks(): void {
    const vw = this.app.renderer.screen.width || 800;
    const vh = this.app.renderer.screen.height || 600;
    const halfW = vw / 2 / this.zoom, halfH = vh / 2 / this.zoom;
    const px = this.save.player.x, py = this.save.player.y;
    const minCx = chunkOf(worldToTile(px - halfW)) - CHUNK_MARGIN;
    const maxCx = chunkOf(worldToTile(px + halfW)) + CHUNK_MARGIN;
    const minCy = chunkOf(worldToTile(py - halfH)) - CHUNK_MARGIN;
    const maxCy = chunkOf(worldToTile(py + halfH)) + CHUNK_MARGIN;
    const wanted = new Set<string>();
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        const key = `${cx},${cy}`; wanted.add(key);
        if (!this.chunkSprites.has(key) && this.chunkSprites.size < MAX_CHUNK_SPRITES) {
          const chunk = this.getChunk(cx, cy);
          const sp = bakeChunkSprite(chunk);
          this.overGround.addChild(sp);
          this.chunkSprites.set(key, sp);
          this.spawnNodes(chunk);
        }
      }
    }
    for (const [key, sp] of this.chunkSprites) {
      if (!wanted.has(key)) { sp.destroy({ texture: true }); this.chunkSprites.delete(key); this.despawnNodes(key); this.corruptionGfx.get(key)?.destroy({ texture: true }); this.corruptionGfx.delete(key); }
    }
  }

  private getChunk(cx: number, cy: number): Chunk {
    const key = `${cx},${cy}`;
    let c = this.chunks.get(key);
    if (!c) { c = genChunk(this.save.seed, cx, cy); this.chunks.set(key, c); }
    return c;
  }

  private spawnNodes(chunk: Chunk): void {
    for (const node of chunk.nodes) {
      if (this.save.world.harvested[node.key] != null) continue;
      if (this.nodeSprites.has(node.key)) continue;
      const sp = new Sprite(tex(node.kind));
      sp.anchor.set(0.5, 0.85);
      sp.x = tileToWorld(node.tx) + TILE / 2;
      sp.y = tileToWorld(node.ty) + TILE / 2;
      sp.zIndex = sp.y;
      let label: Text | undefined;
      if (node.kind === 'entrance') {
        // corrupted-ground halo under the entrance
        const halo = new Sprite(tex('player')); // placeholder tinted circle
        const g = new Graphics(); g.circle(0, 0, CORRUPTION_RADIUS).fill({ color: 0x3a1030, alpha: 0.28 });
        const h = this.app.renderer.generateTexture(g); g.destroy();
        const hs = new Sprite(h); hs.anchor.set(0.5); hs.x = sp.x; hs.y = sp.y; hs.zIndex = 1;
        this.overGround.addChild(hs);
        this.corruptionGfx.set(`${chunk.cx},${chunk.cy}`, hs);
        halo.destroy();
        // floating "Dungeon" label so the entrance is unmistakable (a sibling in the container —
        // Pixi v8 deprecates parenting children to a Sprite)
        label = new Text({ text: '⛓️ Dungeon', style: { fontFamily: 'system-ui', fontSize: 13, fontWeight: '700', fill: 0xd7b0ec, stroke: { color: 0x1a0f22, width: 4 } } });
        label.anchor.set(0.5, 1); label.x = sp.x; label.y = sp.y - 34; label.zIndex = sp.y + 5;
        this.overEnv.addChild(label);
      }
      let glow: Sprite | undefined;
      if (node.kind === 'totem') {
        // totems wake as the grove is restored — a gold glow that brightens with each stage
        glow = new Sprite(tex('glow'));
        glow.anchor.set(0.5); glow.blendMode = 'add'; glow.tint = 0xffd23f; glow.scale.set(1.4);
        glow.x = sp.x; glow.y = sp.y - 6; glow.zIndex = sp.y - 1;
        this.overEnv.addChild(glow);
      }
      this.overEnv.addChild(sp);
      const ns: NodeSprite = { node, sprite: sp, hp: node.maxHp, label, glow };
      this.nodeSprites.set(node.key, ns);
      if (glow) this.refreshTotemGlows();
    }
  }

  /** Brighten the totems' glow in step with grove restoration (dormant at 0, radiant when whole). */
  private refreshTotemGlows(): void {
    const frac = this.save.grove.stage / GROVE_TOTEM_COSTS.length;
    for (const ns of this.nodeSprites.values()) {
      if (ns.glow) ns.glow.alpha = 0.6 * frac;
    }
  }

  private despawnNodes(chunkKey: string): void {
    for (const [key, ns] of this.nodeSprites) if (key.startsWith(chunkKey + ':')) { ns.sprite.destroy(); ns.label?.destroy(); this.nodeSprites.delete(key); }
  }

  solidAtWorld(wx: number, wy: number): boolean {
    const tx = worldToTile(wx), ty = worldToTile(wy);
    if (this.mode === 'dungeon') return this.dunFloor ? isWall(this.dunFloor, tx, ty) : true;
    if (this.walls.has(`${tx},${ty}`)) return true;
    return isSolidTile(tileOfBiome(biomeAt(this.save.seed, tx, ty)));
  }

  /** Nearest walkable tile centre to a point (spiral search) — un-sticks a bad load position. */
  private findWalkable(x: number, y: number): { x: number; y: number } {
    const stx = worldToTile(x), sty = worldToTile(y);
    for (let r = 1; r <= 24; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // ring perimeter only
          const wx = tileToWorld(stx + dx) + TILE / 2, wy = tileToWorld(sty + dy) + TILE / 2;
          if (!this.solidAtWorld(wx, wy)) return { x: wx, y: wy };
        }
      }
    }
    return { x: this.save.home.x, y: this.save.home.y }; // fallback: home
  }

  private moveEntity(m: { x: number; y: number }, dx: number, dy: number): void {
    if (!this.solidAtWorld(m.x + dx, m.y)) m.x += dx;
    if (!this.solidAtWorld(m.x, m.y + dy)) m.y += dy;
  }

  // ---- render ----
  private render(): void {
    const s = this.save;
    // player: face the way you walk (flip L/R; keep last horizontal side while moving up/down)
    const wantTex = tex('player');
    if (this.playerSprite.texture !== wantTex) this.playerSprite.texture = wantTex;
    if (Math.abs(this.facing.x) > 0.2) this.playerFaceSign = this.facing.x < 0 ? -1 : 1;
    this.playerSprite.x = s.player.x; this.playerSprite.y = s.player.y; this.playerSprite.zIndex = s.player.y;
    this.playerSprite.scale.x = this.playerFaceSign;
    this.playerSprite.alpha = s.clock.gameS < this.iframesUntil && Math.floor(s.clock.gameS * 12) % 2 === 0 ? 0.5 : 1;
    // held weapon: the bow, or a sword when the melee slot is armed — both point where you walk
    const armedMelee = s.weapons.melee >= 1 || s.equipped.weapon != null;
    const held = this.weapon === 'bow' ? this.bowSprite : (armedMelee ? this.meleeSprite : undefined);
    for (const w of [this.bowSprite, this.meleeSprite]) if (w) w.visible = false;
    if (held) {
      const a = Math.atan2(this.facing.y, this.facing.x);
      held.visible = true;
      held.x = s.player.x + this.facing.x * 15;
      held.y = s.player.y - 8 + this.facing.y * 15;
      held.rotation = a;
      held.zIndex = s.player.y + (this.facing.y < 0 ? -1 : 1); // behind the player when aiming up
    }
    this.playerSprite.tint = s.clock.gameS < this.playerHurtUntil ? 0xff6a6a : 0xffffff;
    // dungeon torchlight: a warm pool under the player so the near tiles are actually lit
    if (this.mode === 'dungeon') {
      if (!this.dunTorch) {
        this.dunTorch = new Sprite(tex('glow'));
        this.dunTorch.anchor.set(0.5); this.dunTorch.blendMode = 'add'; this.dunTorch.tint = 0xffdca6; this.dunTorch.scale.set(3.4);
        this.actorLayer.addChild(this.dunTorch);
      }
      this.dunTorch.visible = true; this.dunTorch.alpha = 0.6;
      this.dunTorch.x = s.player.x; this.dunTorch.y = s.player.y; this.dunTorch.zIndex = s.player.y - 1e6;
    } else if (this.dunTorch) {
      this.dunTorch.visible = false;
    }
    // nodes ease bump
    this.nodeSprites.forEach((ns) => { if (ns.sprite.scale.x > 1) ns.sprite.scale.set(Math.max(1, ns.sprite.scale.x - 0.01)); });
    // mobs
    for (const inst of this.mobs.values()) {
      const m = inst.mob;
      inst.sprite.x = m.x; inst.sprite.y = m.y; inst.sprite.zIndex = m.y;
      if (inst.aura) { inst.aura.x = m.x; inst.aura.y = m.y - 4; inst.aura.zIndex = m.y - 1; inst.aura.alpha = 0.42 + 0.16 * Math.sin(s.clock.gameS * 3.5); }
      inst.sprite.tint = s.clock.gameS < m.hitFlashUntilGameS ? 0xffffff : (m.state === 'windup' ? 0xff8888 : 0xffffff);
      inst.sprite.scale.set(inst.baseScale * (m.state === 'windup' ? 1.15 : 1));
      // floating name + health bar, riding just above the animal
      const frac = Math.max(0, Math.min(1, m.hp / m.maxHp));
      inst.label.x = m.x; inst.label.y = m.y - (m.isBoss ? 58 : 30); inst.label.zIndex = m.y + 1e6;
      inst.barFill.width = inst.barW * frac;
      inst.barFill.tint = frac > 0.5 ? 0x4caf50 : frac > 0.25 ? 0xe0b020 : 0xd0402a;
    }
    for (const inst of this.drops.values()) { inst.sprite.x = inst.drop.x; inst.sprite.y = inst.drop.y; inst.sprite.zIndex = inst.drop.y; }
    for (const inst of this.projectiles.values()) { inst.sprite.x = inst.proj.x; inst.sprite.y = inst.proj.y; inst.sprite.zIndex = inst.proj.y; }
    // fx lifetimes
    const now = performance.now();
    this.fx = this.fx.filter((f) => {
      if (now >= f.diesAtReal) { f.g.destroy(); return false; }
      if (f.vy) f.g.y += f.vy * 0.016;
      if (f.g instanceof Text) f.g.alpha = Math.max(0, (f.diesAtReal - now) / 700);
      else f.g.alpha = Math.max(0, (f.diesAtReal - now) / 130);
      return true;
    });
    // camera + shake — use the LOGICAL screen size (already CSS px via autoDensity). Dividing by
    // resolution was wrong on high-DPR displays and pushed the player off-centre.
    const vw = this.app.renderer.screen.width;
    const vh = this.app.renderer.screen.height;
    let sx = 0, sy = 0;
    if (now < this.shake.until) { const m = this.shake.mag * ((this.shake.until - now) / 160); sx = (Math.random() - 0.5) * m * 2; sy = (Math.random() - 0.5) * m * 2; }
    const z = this.mode === 'dungeon' ? this.zoom * DUNGEON_ZOOM_MULT : this.zoom;
    this.worldRoot.scale.set(z);
    this.worldRoot.x = vw / 2 - s.player.x * z + sx;
    this.worldRoot.y = vh / 2 - s.player.y * z + sy;
  }

  timeOfDay(): { isNight: boolean; darkness: number; label: string; phaseFrac: number } {
    const t = this.save.clock.gameS % CYCLE_LEN;
    const isNight = t >= DAY_LEN;
    const phaseFrac = isNight ? (t - DAY_LEN) / (CYCLE_LEN - DAY_LEN) : t / DAY_LEN;
    const d = isNight ? Math.sin(Math.min(1, phaseFrac) * Math.PI) * 0.55 : 0;
    return { isNight, darkness: d, label: isNight ? 'Night' : 'Day', phaseFrac };
  }

  homeVector(): { angleRad: number; distTiles: number } {
    const dx = this.save.home.x - this.save.player.x, dy = this.save.home.y - this.save.player.y;
    return { angleRad: Math.atan2(dy, dx), distTiles: Math.round(Math.hypot(dx, dy) / TILE) };
  }
  /** In a dungeon, the direction to the objective — the boss while it lives, else the stairs down. */
  dungeonVector(): { angleRad: number; distTiles: number; target: 'boss' | 'stairs' } {
    const s = this.save;
    const boss = this.bossEid > 0 ? this.mobs.get(this.bossEid) : undefined;
    const target: 'boss' | 'stairs' = boss ? 'boss' : 'stairs';
    const tx = boss ? boss.mob.x : tileToWorld(this.stairsTile.tx) + TILE / 2;
    const ty = boss ? boss.mob.y : tileToWorld(this.stairsTile.ty) + TILE / 2;
    const dx = tx - s.player.x, dy = ty - s.player.y;
    return { angleRad: Math.atan2(dy, dx), distTiles: Math.round(Math.hypot(dx, dy) / TILE), target };
  }

  private lastSaveGameS = 0;
  private maybeAutosave(): void {
    if (this.save.clock.gameS - this.lastSaveGameS >= 10) { this.lastSaveGameS = this.save.clock.gameS; this.persist(); }
  }
  persist(): void {
    this.save.lastSeenMs = Date.now();
    const status = writeSave(this.save);
    if (status === 'soft') this.emit('toast', { icon: '💾', text: 'Save is getting large' });
    if (status === 'error') this.emit('toast', { icon: '⚠️', text: 'Saving unavailable (private mode?)' });
    this.emit('saved'); // the cloud layer (when signed in) debounces + pushes off this signal
  }

  private trackFps(dt: number): void {
    this.lastPerf.frames++; this.lastPerf.t += dt;
    if (this.lastPerf.t >= 0.5) { this.lastPerf.fps = Math.round(this.lastPerf.frames / this.lastPerf.t); this.lastPerf.frames = 0; this.lastPerf.t = 0; }
  }
  perf() { return { fps: this.lastPerf.fps, chunkSprites: this.chunkSprites.size, mainChildren: this.actorLayer.children.length, nodes: this.nodeSprites.size, mobs: this.mobs.size }; }

  snapshot() {
    const s = this.save;
    const st = this.stats();
    const food = resolveFood(s.buffs.food, s.clock.gameS, MAX_FOOD_BUFFS);
    const time = this.timeOfDay();
    const nearFire = this.nearCampfire();
    // a lit camp pushes back the night (relief on the darkness overlay)
    if (nearFire) time.darkness *= 0.35;
    return {
      seed: s.seed,
      player: { ...s.player, maxHp: st.maxHp, fed: food.active.length > 0, iframes: s.clock.gameS < this.iframesUntil },
      inventory: { ...s.inventory },
      clockS: Math.round(s.clock.gameS * 10) / 10,
      time,
      home: this.homeVector(),
      dungeonNav: this.mode === 'dungeon' ? this.dungeonVector() : null,
      skills: { wood: xpToLevel(s.skills.wood), mine: xpToLevel(s.skills.mine), combat: xpToLevel(s.skills.combat), craft: xpToLevel(s.skills.craft) },
      skillXp: { ...s.skills },
      tools: { ...s.tools },
      weapons: { ...s.weapons },
      armor: s.armor,
      atk: Math.round(st.atk * 10) / 10,
      def: st.def,
      foodBuffs: food.active.length,
      warmed: s.clock.gameS < s.buffs.warmedUntilGameS,
      nearCampfire: nearFire,
      placed: s.world.placed.length,
      weapon: this.weapon,
      mobs: this.mobs.size,
      mode: this.mode,
      floor: this.floor,
      deepest: s.dungeon.deepest,
      checkpoints: [...s.dungeon.checkpoints],
      pity: s.dungeon.pity,
      satchel: { gold: s.satchel.gold, items: { ...s.satchel.items }, gear: s.satchel.gear.length },
      gear: s.gear.map((g) => ({ uid: g.uid, name: g.name, slot: g.slot, rarity: g.rarity, legendary: g.legendary, motif: g.motif, empowerLevel: g.empower.level, crafted: g.crafted, icon: g.icon, equipped: Object.values(s.equipped).includes(g.uid) })),
      equipped: { ...s.equipped },
      relics: s.relics.map((r) => ({ uid: r.uid, effect: r.effect, name: r.name, rarity: r.rarity, floor: r.floor, equipped: s.equippedRelics.includes(r.uid) })),
      equippedRelics: [...s.equippedRelics],
      crit: st.critPct,
      legendaries: [...this.equippedLegendaries],
      motif: { ...this.motif },
      tier: s.dungeon.tier,
      codex: { mobs: [...s.codex.mobs], recipes: [...s.codex.recipes], relics: [...s.codex.relics] },
      grove: { fed: s.grove.fed, stage: s.grove.stage, max: GROVE_TOTEM_COSTS.length, next: groveThreshold(s.grove.stage + 1), saturation: this.groveSaturation() },
      bests: { ...s.dungeon.bests },
      blessing: { banked: s.blessing.banked, today: todaysBlessing(Date.now()).label, claimable: s.blessing.banked > 0 && s.blessing.claimedDayUTC !== dayUTC(Date.now()), active: this.blessingActive(s.blessing.activeId ?? '') ? s.blessing.activeId : null },
      bossAlive: this.bossEid > 0,
      pendingBlessing: this.pendingBlessing,
      pendingPortal: this.pendingPortal,
      safe: this.inSafeZone(),
      sanctuary: s.sanctuary,
      sanctuaryMax: SANCTUARY_MAX_LEVEL,
      sanctuaryCost: this.sanctuaryCost(),
      totems: [...s.totems],
      biome: biomeAt(s.seed, worldToTile(s.player.x), worldToTile(s.player.y)),
    };
  }
}

export function loadOrNull(): SaveData | null { return readSave(); }
